// ── Плеер: волна, карточки по ходу аудио, текст, настройки воспроизведения ─────

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import CardTranslationField from "../../CardSuggest";
import { CircleBtn, IC, IconBtn } from "../../components/Bits";
import { SpeedPopup, fmtSpeed, speedLabel } from "../../components/Speed";
import { t } from "../../i18n";
import Icon from "../../Icon";
import { appIconDataUri, mediaSession } from "../../mediaSession";
import { readPlayPos, savePlayPos } from "../../playPos";
import { markSetting } from "../../settings";
import { CARD_COLORS, FONT, SANS, cardColorIn, uiFontFamily } from "../../themes";
import { Chip, Field, Modal, Slider, Button as UiButton, IconButton as UiIconButton, notify, useConfirm, usePrompt } from "../../ui";
import { legibleOn, readableOn } from "../../ui/color";
import { paintWave } from "./wave";
import { countWords, fmt, isNative, nCards, nWords, parseSubtitles, parseTC, recordTime, resolveLang } from "../../utils";
import VolumeControl from "../../VolumeControl";
import TextImport from "../TextImport";
import { CardRow } from "./CardRow";
import { usePalette } from "../../ui/theme";
import { fieldSurface } from '../../ui/surface';
import { panelSurface } from '../../ui/surface';

// ── IDB-кэш волновой формы (переживает перезагрузку страницы) ────────────────
export const _idbWave=(()=>{
  const DB="ac_wave",ST="peaks";
  const open=()=>new Promise((res,rej)=>{const r=indexedDB.open(DB,1);r.onupgradeneeded=e=>e.target.result.createObjectStore(ST);r.onsuccess=e=>res(e.target.result);r.onerror=rej;});
  return{
    async get(url){try{const db=await open();return await new Promise(res=>{const t=db.transaction(ST,"readonly"),s=t.objectStore(ST).get(url);s.onsuccess=()=>{db.close();res(s.result||null);};s.onerror=()=>{db.close();res(null);};});}catch{return null;}},
    async set(url,val){try{const db=await open();await new Promise(res=>{const t=db.transaction(ST,"readwrite");t.objectStore(ST).put(val,url);t.oncomplete=()=>{db.close();res();};t.onerror=()=>{db.close();res();};});}catch{}},
  };
})();

// ── Плавающая карточка: её верхняя ручка (с кнопкой «на место») всегда на экране ──
// Раньше карточку можно было утащить за верх экрана телефона — и вернуть было нечем.
export function clampFloatPos(p){
  if(!p||typeof window==="undefined")return p;
  const vw=window.innerWidth,vh=window.innerHeight;
  return{x:Math.min(Math.max(p.x,-200),vw-80),y:Math.min(Math.max(p.y,0),vh-48)};
}

// ── Длина аудио, у которого её нет в заголовке ───────────────────────────────
// Файлы, сжатые в браузере в Opus/WebM до исправления, не содержат длительности:
// браузер сообщает Infinity, ползунок не двигается. Классический обход — перемотать
// далеко за конец: браузер дочитает файл, узнает длину, и мы возвращаемся назад.
export function withFiniteDuration(a,cb){
  if(Number.isFinite(a.duration)&&a.duration>0){cb(a.duration);return;}
  const back=a.currentTime||0;
  const done=()=>{
    if(!Number.isFinite(a.duration))return;
    a.removeEventListener("durationchange",done);a.removeEventListener("timeupdate",done);
    a.currentTime=back;a._acProbing=false;cb(a.duration);
  };
  a.addEventListener("durationchange",done);a.addEventListener("timeupdate",done);
  // Пока узнаём длину, ползунок не трогаем (иначе он прыгал в конец).
  a._acProbing=true;
  try{a.currentTime=1e101;}catch{a._acProbing=false;}
}

// ── Логотип для уведомления на телефоне ────────────────────────────────────────
// Размер картинки в медиа-уведомлении задаёт сам Android: снимок карточки там мелкий и
// обновлялся с опозданием. На телефоне слово и перевод видны текстом, а картинка —
// логотип приложения (data URI: нативный плагин не скачивает по ссылке).

// Пульт уведомления «что играет» для большого плеера (см. mediaSession.js).
const ms=mediaSession('player');

// ── Player — главный экран плеера ────────────────────────────────────────────
// Таймлайн, волновая форма, плавающая карточка, AB-повтор, MediaSession.
export function Player({onCreateLinkedText,project,audioSrc,audioOwnerId,onSave,onBack,theme,onTheme,onQuiz,visible,onStateChange,onLoadAudio,onBeforePlay,downloadStatus={},onDownload,onDeleteOffline,initialSeek=null,onSeekDone,linkedTextProject=null,onOpenTextProject,onLinkTextToAudio,textProjects=[],onCardChange=null,autoPlayReq=0,onEnded=null,queueCount=0,onOpenQueue=null}){
  const P=usePalette();
  const [dur,setDur]=useState(0);
  const [ask,confirmEl]=useConfirm(P);
  const [promptEl,askText]=usePrompt(P);
  const [cur,setCur]=useState(0);
  const [playing,setPlaying]=useState(false);
  const [cards,setCards]=useState(project?.cards||[]);
  const [editing,setEditing]=useState(null);
  const [tcInput,setTcInput]=useState("");
  const [activeCard,setActiveCard]=useState(null);
  const [flipped,setFlipped]=useState(false);
  const [pausedByFlip,setPausedByFlip]=useState(false);
  const [cardAnim,setCardAnim]=useState(false);
  const [shownIds,setShownIds]=useState(new Set());
  const [skip,setSkip]=useState(5);
  // Громкость запоминается между запусками, как в YouTube.
  const [vol,setVol]=useState(()=>{try{const v=parseFloat(localStorage.getItem("ac_volume"));return Number.isFinite(v)?Math.min(1,Math.max(0,v)):1;}catch{return 1;}});
  const [muted,setMuted]=useState(false);
  useEffect(()=>{try{localStorage.setItem("ac_volume",String(vol));}catch{}},[vol]);
  const [looping,setLooping]=useState(false);
  const [pauseOnCard,setPauseOnCard]=useState(false);
  const [pauseDelay,setPauseDelay]=useState(0.4);
  const [speed,setSpeed]=useState(1.0);
  // Клип (как клипы в Twitch): одна клавиша зацикливает последние N секунд.
  // Замедление: пока держишь кнопку или S — звук идёт медленно, отпустил — обычная скорость.
  // Обе настройки запоминаются навсегда (и синхронизируются между устройствами).
  const [clipLen,setClipLen]=useState(()=>{const v=parseFloat(localStorage.getItem("ac_clip_len"));return v>=1&&v<=15?v:5;});
  const [slowRate,setSlowRate]=useState(()=>{const v=parseFloat(localStorage.getItem("ac_slow_rate"));return v>=0.3&&v<=0.95?v:0.6;});
  const [slowHeld,setSlowHeld]=useState(false);
  const [nameVal,setNameVal]=useState(project?.name||"");
  const [renamingProj,setRenamingProj]=useState(false);
  const [hovPin,setHovPin]=useState(null);
  const [touchPin,setTouchPin]=useState(null);
  const [abA,setAbA]=useState(null);
  const [abB,setAbB]=useState(null);
  const [abActive,setAbActive]=useState(false);
  const [showSettings,setShowSettings]=useState(false);
  const [showTextImport,setShowTextImport]=useState(false);
  const [showSpeedPopup,setShowSpeedPopup]=useState(false);
  const [selectedCards,setSelectedCards]=useState(new Set());
  const [selectMode,setSelectMode]=useState(false);
  const [wideLayout,setWideLayout]=useState(()=>window.innerWidth>=1100);
  const [pinnedCards,setPinnedCards]=useState([]);
  // Окно сузили — закреплённые карточки перерисовываются и прижимаются к краю, а не остаются за ним.
  const [,setViewportW]=useState(0);
  useEffect(()=>{if(!pinnedCards.length)return;const on=()=>setViewportW(window.innerWidth);window.addEventListener("resize",on);return()=>window.removeEventListener("resize",on);},[pinnedCards.length]);
  const [floatCardSize,setFloatCardSize]=useState(()=>{try{const s=localStorage.getItem("ac_float_card_size");return s?JSON.parse(s):{w:340,h:null};}catch{return{w:340,h:null};}});
  // null = inline below settings; {x,y} = dragged to fixed position
  const [floatCardPos,setFloatCardPos]=useState(()=>{try{const s=localStorage.getItem("ac_float_card_pos");return s?clampFloatPos(JSON.parse(s)):null;}catch{return null;}});
  const [autoScroll,setAutoScroll]=useState(false);
  const [showLinkedText,setShowLinkedText]=useState(false);
  const [textDraft,setTextDraft]=useState("");
  const [textSaving,setTextSaving]=useState(false);

  const audioRef=useRef(null),tlRef=useRef(null),waveRef=useRef(null),waveHotRef=useRef(null);
  const prevT=useRef(0),dragRef=useRef(false),abDragRef=useRef(null),longTimer=useRef(null);
  const skipRef=useRef(skip),seekToRef=useRef(null),cardsRef=useRef(cards),pendingSeekRef=useRef(null);
  const nameRef=useRef(nameVal),playRef=useRef(playing),abARef=useRef(abA),abBRef=useRef(abB),abActiveRef=useRef(abActive),pauseDelayRef=useRef(pauseDelay);
  // ── jumpCardRef: всегда актуальная ссылка на jumpCard, без stale closure ──
  const jumpCardRef=useRef(null);
  const audioOwnerIdRef=useRef(audioOwnerId);
  const projectIdRef=useRef(project?.id);
  const lastPosSaveRef=useRef(0);
  const rowFns=useRef({}); // свежие функции плеера для строк списка карточек (см. CardRow)
  useEffect(()=>{audioOwnerIdRef.current=audioOwnerId;},[audioOwnerId]);
  useEffect(()=>{projectIdRef.current=project?.id;},[project?.id]);
  const srtRef=useRef(null),touchPinTimer=useRef(null);
  const pinDragRef=useRef(null),pinResizeRef=useRef(null),floatResizeRef=useRef(null);
  const floatDragStartRef=useRef(null);
  const isProgramScroll=useRef(false);
  const artworkBlobUrlRef=useRef(null); // текущий Blob URL обложки — revoke при смене
  const hasEverPlayedRef=useRef(false); // guard: не стартуем сервис пока юзер не нажал play

  useEffect(()=>{pauseDelayRef.current=pauseDelay;},[pauseDelay]);
  useEffect(()=>{cardsRef.current=cards;},[cards]);
  useEffect(()=>{nameRef.current=nameVal;},[nameVal]);
  useEffect(()=>{playRef.current=playing;},[playing]);
  useEffect(()=>{abARef.current=abA;},[abA]);
  useEffect(()=>{abBRef.current=abB;},[abB]);
  useEffect(()=>{abActiveRef.current=abActive;},[abActive]);
  useEffect(()=>{skipRef.current=skip;},[skip]);
  const speedRef=useRef(speed),clipLenRef=useRef(clipLen),slowRateRef=useRef(slowRate),slowHeldRef=useRef(false);
  useEffect(()=>{speedRef.current=speed;},[speed]);
  useEffect(()=>{clipLenRef.current=clipLen;},[clipLen]);
  useEffect(()=>{slowRateRef.current=slowRate;},[slowRate]);
  // Сохраняем только когда пользователь двигает ползунок (а не при каждом открытии плеера),
  // чтобы не затирать значение, выставленное на другом устройстве.
  const saveClipLen=(v)=>{setClipLen(v);try{localStorage.setItem("ac_clip_len",String(v));markSetting("ac_clip_len");}catch{}};
  const saveSlowRate=(v)=>{setSlowRate(v);try{localStorage.setItem("ac_slow_rate",String(v));markSetting("ac_slow_rate");}catch{}};

  // Overlay 🔁 button activates the Player's own AB loop (same mechanism, same UI markers).
  // Refs are set directly so the timeupdate handler sees them immediately without waiting for render.
  useEffect(()=>{
    const handler=(e)=>{
      if(e.detail?.active){
        setAbActive(true);abActiveRef.current=true;
        setAbA(e.detail.a);abARef.current=e.detail.a;
        setAbB(e.detail.b);abBRef.current=e.detail.b;
      }else{
        setAbActive(false);abActiveRef.current=false;
        setAbA(null);abARef.current=null;
        setAbB(null);abBRef.current=null;
      }
    };
    window.addEventListener('overlay_ab_loop',handler);
    return()=>window.removeEventListener('overlay_ab_loop',handler);
  },[]);

  // Что плеер последний раз сохранил для ЭТОГО проекта: удалёнными считаются только
  // карточки, которые исчезли из этого списка в самом плеере (а не всё, чего нет
  // в чужой, возможно устаревшей копии проекта — так однажды пропали 263 карточки).
  const savedIdsRef=useRef(new Set((project?.cards||[]).map(c=>c.id)));
  const bulkDeleteRef=useRef(false);
  useEffect(()=>{if(project){savedIdsRef.current=new Set((project.cards||[]).map(c=>c.id));setCards(project.cards||[]);setNameVal(project.name||"");setShownIds(new Set());setActiveCard(null);setEditing(null);}},[project?.id]);
  // Тот же проект пришёл с другим списком карточек (свежая версия после синхронизации,
  // повторное открытие) — берём его, не сбрасывая открытую карточку и прочее.
  useEffect(()=>{
    if(!project?.cards||project.cards===cardsRef.current)return;
    savedIdsRef.current=new Set(project.cards.map(c=>c.id));
    setCards(project.cards);
  },[project?.cards]);// eslint-disable-line react-hooks/exhaustive-deps

  // Restore pinned cards per project
  useEffect(()=>{if(project?.id){try{const s=localStorage.getItem(`ac_pins_${project.id}`);setPinnedCards(s?JSON.parse(s):[]);}catch{setPinnedCards([]);}}else{setPinnedCards([]);};},[project?.id]);
  // Persist pinned cards
  useEffect(()=>{if(project?.id){try{localStorage.setItem(`ac_pins_${project.id}`,JSON.stringify(pinnedCards));}catch{}};},[pinnedCards,project?.id]);
  // Persist float card size
  useEffect(()=>{try{localStorage.setItem("ac_float_card_size",JSON.stringify(floatCardSize));}catch{};},[floatCardSize]);
  // Persist float card position
  useEffect(()=>{try{if(floatCardPos)localStorage.setItem("ac_float_card_pos",JSON.stringify(floatCardPos));else localStorage.removeItem("ac_float_card_pos");}catch{};},[floatCardPos]);
  // Responsive layout (и плавающая карточка не должна остаться за краем после поворота экрана)
  useEffect(()=>{const h=()=>{setWideLayout(window.innerWidth>=1100);setFloatCardPos(p=>p?clampFloatPos(p):p);};window.addEventListener("resize",h);return()=>window.removeEventListener("resize",h);},[]);
  // Global drag/resize handlers for pinned cards
  const _dragRafRef=useRef(null);
  useEffect(()=>{
    const onMove=(e)=>{
      const isDragging=pinDragRef.current||pinResizeRef.current||floatResizeRef.current||floatDragStartRef.current;
      // Блокируем скролл страницы пока тащим карточку на тач-устройстве
      if(isDragging&&e.cancelable){e.preventDefault();}
      const mx=e.clientX!==undefined?e.clientX:e.touches?.[0]?.clientX;
      const my=e.clientY!==undefined?e.clientY:e.touches?.[0]?.clientY;
      if(mx===undefined)return;
      // requestAnimationFrame для плавности — не вызываем setState чаще чем 60fps
      if(_dragRafRef.current)cancelAnimationFrame(_dragRafRef.current);
      _dragRafRef.current=requestAnimationFrame(()=>{
        if(pinDragRef.current){
          const{uid,smx,smy,sx,sy}=pinDragRef.current;
          setPinnedCards(prev=>prev.map(p=>p.uid===uid?{...p,x:sx+(mx-smx),y:sy+(my-smy)}:p));
        }
        if(pinResizeRef.current){
          const{uid,smx,smy,sw,sh}=pinResizeRef.current;
          setPinnedCards(prev=>prev.map(p=>p.uid===uid?{...p,w:Math.max(150,sw+(mx-smx)),h:Math.max(80,sh+(my-smy))}:p));
        }
        if(floatResizeRef.current){
          const{smx,smy,sw,sh}=floatResizeRef.current;
          setFloatCardSize({w:Math.max(200,sw+(mx-smx)),h:sh?Math.max(120,sh+(my-smy)):null});
        }
        if(floatDragStartRef.current){
          const{smx,smy,sx,sy}=floatDragStartRef.current;
          setFloatCardPos(clampFloatPos({x:sx+(mx-smx),y:sy+(my-smy)}));
        }
      });
    };
    const onUp=()=>{
      pinDragRef.current=null;pinResizeRef.current=null;
      floatResizeRef.current=null;floatDragStartRef.current=null;
    };
    window.addEventListener("mousemove",onMove);window.addEventListener("mouseup",onUp);
    // passive:false — нужно чтобы вызывать preventDefault() и блокировать скролл при перетаскивании
    window.addEventListener("touchmove",onMove,{passive:false});window.addEventListener("touchend",onUp);
    return()=>{
      window.removeEventListener("mousemove",onMove);window.removeEventListener("mouseup",onUp);
      window.removeEventListener("touchmove",onMove);window.removeEventListener("touchend",onUp);
    };
  },[]);
  // Новое аудио: длина прежнего больше не показывается (было «0:00 / 11:53» у 15-секундной записи), пока не придут данные нового.
  useEffect(()=>{const a=audioRef.current;if(!a||!audioSrc)return;a.src=audioSrc;setCur(0);setDur(0);setPlaying(false);
    // Продолжаем с места, где остановились (если нет явного перехода — из поиска или PTR).
    const pid=projectIdRef.current;
    const saved=initialSeek==null&&pid&&(!audioOwnerIdRef.current||audioOwnerIdRef.current===pid)?readPlayPos(pid):0;
    if(saved>0){
      const apply=()=>{
        if(!Number.isFinite(a.duration)){a.addEventListener("durationchange",apply,{once:true});return;} // длина ещё вычисляется
        if(a.duration&&saved<a.duration-5){a.currentTime=saved;prevT.current=saved;setCur(saved);}
      };
      if(a.readyState>=1)apply();else a.addEventListener("loadedmetadata",apply,{once:true});
    }
  },[audioSrc]);// eslint-disable-line react-hooks/exhaustive-deps
  // ▶ на карточке «Продолжить»: играть сразу, не открывая плеер (звук — в мини-плеере).
  const autoPlayDoneRef=useRef(0);
  // Конец аудио → App включает следующее из очереди.
  const onEndedRef=useRef(onEnded);
  onEndedRef.current=onEnded;
  useEffect(()=>{
    if(!autoPlayReq||autoPlayDoneRef.current===autoPlayReq)return;
    const a=audioRef.current;if(!a||!audioSrc)return;
    if(audioOwnerIdRef.current&&projectIdRef.current&&audioOwnerIdRef.current!==projectIdRef.current)return;
    autoPlayDoneRef.current=autoPlayReq;
    const go=()=>{onBeforePlay?.();a.play().then(()=>setPlaying(true)).catch(()=>setPlaying(false));};
    if(a.readyState>=2)go();else a.addEventListener("canplay",go,{once:true});
  },[autoPlayReq,audioSrc]);// eslint-disable-line react-hooks/exhaustive-deps
  // Выполняем initialSeek (из поиска или PTR) после загрузки метаданных
  useEffect(()=>{
    if(initialSeek==null||initialSeek<=0)return;
    const a=audioRef.current;if(!a)return;
    const apply=()=>{
      if(!Number.isFinite(a.duration)){a.addEventListener("durationchange",apply,{once:true});return;} // длина ещё вычисляется
      if(a.duration&&!isNaN(a.duration)&&a.duration>0){
        const t=Math.min(initialSeek,a.duration);
        a.currentTime=t;prevT.current=t;setCur(t);
        onSeekDone?.();
      }
    };
    if(a.readyState>=1)apply();
    else a.addEventListener("loadedmetadata",apply,{once:true});
    return()=>a.removeEventListener("loadedmetadata",apply);
  },[initialSeek]);
  useEffect(()=>{const a=audioRef.current;if(!a)return;a.loop=looping&&!abActive;},[looping,abActive]);
  useEffect(()=>{const a=audioRef.current;if(!a)return;a.volume=muted?0:vol;},[vol,muted]);
  useEffect(()=>{const a=audioRef.current;if(!a)return;a.playbackRate=speed;},[speed]);

  // ─── Генератор artwork карточки ─────────────────────────────────────────────
  // 800×800 JPEG (~70-100 KB) — круговой дизайн (Android 12+ показывает largeIcon кругом).
  // Весь контент внутри безопасного радиуса CR; тёмные углы холста срезаются кругом без потерь.
  // Возвращает { blobUrl, dataUri }:
  // blobUrl — для navigator.mediaSession (Chrome / PWA)
  // dataUri — для нативного AudioCardsMedia плагина (base64, декодируется в Java)
  const buildCardArtwork=useCallback((card,projName)=>new Promise(resolve=>{
    if(!card?.word){resolve({blobUrl:null,dataUri:null});return;}
    try{
      const S=800;
      const CX=S/2, CY=S/2;
      const R=S/2-6;   // радиус круга (6px отступ от края холста)
      const CR=R-32;   // радиус безопасной зоны контента (внутри кольца)
      const cv=document.createElement("canvas"); cv.width=S; cv.height=S;
      const ctx=cv.getContext("2d");
      const cardColor=card.color||"#3b82f6";

      // Тёмный фон для JPEG-углов (срезаются при круговом отображении)
      ctx.fillStyle="#080e1c";
      ctx.fillRect(0,0,S,S);

      // Круговой градиентный фон карточки
      ctx.save();
      ctx.beginPath();
      ctx.arc(CX,CY,R,0,Math.PI*2);
      ctx.clip();
      const grad=ctx.createLinearGradient(0,0,S,S);
      grad.addColorStop(0,"#0d1526"); grad.addColorStop(1,"#1b2f4e");
      ctx.fillStyle=grad; ctx.fill();
      ctx.restore();

      // Цветное кольцо-акцент (заменяет прямоугольные полосы которые обрезались по углам)
      ctx.beginPath();
      ctx.arc(CX,CY,R-5,0,Math.PI*2);
      ctx.strokeStyle=cardColor; ctx.lineWidth=12; ctx.stroke();

      // Лёгкий внутренний свет
      const glow=ctx.createRadialGradient(CX,CY,0,CX,CY,CR*0.7);
      glow.addColorStop(0,"rgba(255,255,255,0.06)"); glow.addColorStop(1,"rgba(0,0,0,0)");
      ctx.fillStyle=glow; ctx.fillRect(0,0,S,S);

      // Название проекта (маленький текст, верхняя часть круга)
      const projLabel=(projName||"AntonCards").substring(0,32).toUpperCase();
      ctx.textAlign="center"; ctx.textBaseline="top";
      ctx.fillStyle="rgba(255,255,255,0.42)";
      const projFontPx=Math.round(S*0.034);
      ctx.font=`bold ${projFontPx}px ${uiFontFamily()}`;
      ctx.fillText(projLabel,CX,CY-CR+12);

      const fitText=(text,mW,mH,maxFontPx,minFontPx=16)=>{
        let fs=maxFontPx;
        const lh=()=>fs*1.25;
        const getLines=fnt=>{
          ctx.font=fnt;
          const words=text.split(" "); const lines=[]; let l="";
          for(const w of words){const t=l?l+" "+w:w;if(ctx.measureText(t).width>mW&&l){lines.push(l);l=w;}else l=t;}
          if(l)lines.push(l); return lines;
        };
        while(fs>minFontPx){
          const fnt=`bold ${fs}px ${uiFontFamily()}`;
          const ls=getLines(fnt);
          if(ls.length*lh()<=mH)return{lines:ls,fs,lh:lh()};
          fs=Math.max(minFontPx,fs-4);
        }
        const fnt=`bold ${fs}px ${uiFontFamily()}`;
        return{lines:getLines(fnt).slice(0,Math.floor(mH/lh())),fs,lh:lh()};
      };

      const hasTransl=!!(card.translation?.trim());
      const projAreaH=projFontPx*1.4+16;
      const contentTop=CY-CR+projAreaH;
      const contentBot=CY+CR-14;
      const totalH=contentBot-contentTop;
      const maxW=Math.round(CR*2*0.78); // безопасная ширина внутри круга

      const wordAreaH=hasTransl?totalH*0.46:totalH*0.68;
      const {lines:wLines,fs:wFs,lh:wLh}=fitText(card.word||"",maxW,wordAreaH,Math.round(S*0.115),16);

      ctx.textBaseline="middle";
      ctx.fillStyle="#ffffff";
      ctx.font=`bold ${wFs}px ${uiFontFamily()}`;
      const wordBlockH=wLines.length*wLh;
      const wordCenterY=contentTop+wordAreaH/2;
      wLines.forEach((ln,i)=>{ctx.fillText(ln,CX,wordCenterY-wordBlockH/2+(i+0.5)*wLh);});

      if(hasTransl){
        const divY=contentTop+wordAreaH+10;
        ctx.strokeStyle=`${cardColor}70`; ctx.lineWidth=1.5;
        ctx.beginPath(); ctx.moveTo(CX-CR*0.40,divY); ctx.lineTo(CX+CR*0.40,divY); ctx.stroke();

        const transAreaTop=divY+10;
        const transAreaH=contentBot-transAreaTop;
        const {lines:tLines,fs:tFs,lh:tLh}=fitText(card.translation,maxW,transAreaH,Math.round(S*0.052),14);
        ctx.fillStyle="rgba(255,255,255,0.72)";
        ctx.font=`${tFs}px ${uiFontFamily()}`;
        const transBlockH=tLines.length*tLh;
        const transCenterY=transAreaTop+transAreaH/2;
        tLines.forEach((ln,i)=>{ctx.fillText(ln,CX,transCenterY-transBlockH/2+(i+0.5)*tLh);});
      }

      const dataUri=cv.toDataURL("image/jpeg",0.78);
      cv.toBlob(blob=>{
        if(!blob){resolve({blobUrl:null,dataUri});return;}
        resolve({blobUrl:URL.createObjectURL(blob),dataUri});
      },"image/jpeg",0.78);
    }catch(e){resolve({blobUrl:null,dataUri:null});}
  }),[]);

  useEffect(()=>{
    // ── Регистрируем кнопки уведомления ОДИН РАЗ (deps=[]).
    // Все callback-и читают актуальное состояние через ref-ы, поэтому stale closure исключён.
    // Порядок: ⏮ | ⏸/▶ | ⏭ — именно эти 3 кнопки попадают в компактное уведомление.
    ms.setActionHandler("previoustrack",()=>{jumpCardRef.current?.("prev");});
    ms.setActionHandler("pause",()=>{audioRef.current?.pause();setPlaying(false);});
    ms.setActionHandler("play",()=>{
      if(audioRef.current){audioRef.current.play().catch(()=>{});setPlaying(true);}
    });
    ms.setActionHandler("nexttrack",()=>{jumpCardRef.current?.("next");});
    ms.setActionHandler("seekto",(d)=>{if(d?.seekTime!=null)seekToRef.current?.(d.seekTime);});
    // seekbackward/seekforward намеренно НЕ регистрируем: Chrome Android отдаёт приоритет
    // этим обработчикам и показывает ⏪⏩ вместо ⏮⏭ в компактном уведомлении.
    return()=>{ ms.clearActionHandlers(); };
  },[]);// eslint-disable-line react-hooks/exhaustive-deps

  // ── Слушаем NOTIFICATION_ACTION от Service Worker ─────────────────────────
  // Когда пользователь тапает уведомление при закрытом экране, SW фокусирует
  // вкладку и посылает postMessage — здесь обрабатываем prev/next/play.
  useEffect(()=>{
    const onMsg=(e)=>{
      if(e.data?.type!=="NOTIFICATION_ACTION")return;
      const act=e.data.action;
      if(act==="prev")jumpCardRef.current?.("prev");
      else if(act==="next")jumpCardRef.current?.("next");
      else if(act==="play"||act==="pause"){
        const a=audioRef.current;if(!a)return;
        if(a.paused){a.play().catch(()=>{});setPlaying(true);}
        else{a.pause();setPlaying(false);}
      }
    };
    navigator.serviceWorker?.addEventListener("message",onMsg);
    return()=>navigator.serviceWorker?.removeEventListener("message",onMsg);
  },[]);// eslint-disable-line react-hooks/exhaustive-deps

  // ─── Обновляем уведомление при смене активной карточки ─────────────────
  // Это ключевое: уведомление немедленно показывает слово/перевод
  useEffect(()=>{
    let cancelled=false;
    (async()=>{
      // Определяем карточку для artwork
      let card=activeCard;
      if(!card){
        const sorted=cardsRef.current?.length?[...cardsRef.current].sort((a,b)=>a.time_sec-b.time_sec):[];
        const next=sorted.find(c=>c.time_sec>=(cur||0));
        card=next||sorted[0];
      }
      if(isNative()){
        const logo=await appIconDataUri();
        if(cancelled)return;
        ms.setMetadata({
          title:card?.word||nameVal||"AntonCards",
          artist:card?(card.translation||""):"AntonCards",
          album:nameVal||"AntonCards",
          artwork:[],
          capArtworkDataUri:logo,
        });
        return;
      }
      if(card){
        // Сначала: сразу шлём заголовок/исполнителя (без artwork) — уведомление показывает текст немедленно.
        ms.setMetadata({
          title:card.word||nameVal||"AntonCards",
          artist:card.translation||"",
          album:nameVal||"AntonCards",
          artwork:[],
          capArtworkDataUri:null,
        });
        const {blobUrl,dataUri}=await buildCardArtwork(card,nameVal);
        if(cancelled){if(blobUrl)URL.revokeObjectURL(blobUrl);return;}
        // Revoke предыдущий Blob URL чтобы не утекала память
        if(artworkBlobUrlRef.current)URL.revokeObjectURL(artworkBlobUrlRef.current);
        artworkBlobUrlRef.current=blobUrl;
        // blobUrl — для navigator.mediaSession; dataUri — для Capacitor нативного плагина
        const webSrc=blobUrl||dataUri;
        ms.setMetadata({
          title:card.word||nameVal||"AntonCards",
          artist:card.translation||"",
          album:nameVal||"AntonCards",
          artwork:webSrc?[{src:webSrc,sizes:"512x512",type:"image/jpeg"}]:[],
          capArtworkDataUri:dataUri,
        });
      }else{
        // Нет карточек — просто название проекта (без HTTP URL — плагин не умеет скачивать с localhost)
        ms.setMetadata({
          title:nameVal||"AntonCards",
          artist:"AntonCards",
          album:nameVal||"AntonCards",
          artwork:[],
          capArtworkDataUri:null,
        });
      }
    })();
    return()=>{cancelled=true;};
  },[activeCard,nameVal]);// eslint-disable-line react-hooks/exhaustive-deps

  // Наверх — только редкие изменения (play/pause, длина, скорость, громкость). Время
  // мини-плеер читает сам из audio: раньше всё приложение перерисовывалось 4 раза в секунду.
  useEffect(()=>{onStateChange&&onStateChange({playing,dur,speed,vol,audio:audioRef.current,toggle:togglePlay,pause:()=>{audioRef.current?.pause();setPlaying(false);},seek:seekToRef.current,jumpCard:(d)=>jumpCardRef.current?.(d),_setSpeed:setSpeed,_setVol:setVol,muted,_toggleMute:()=>setMuted(m=>!m),
    skip,clipLen,slowRate,clipOn:abActive,toggleClip,holdSlow});},[playing,dur,speed,vol,muted,skip,clipLen,slowRate,abActive]);// eslint-disable-line react-hooks/exhaustive-deps
  // Сервис стартует только после первого нажатия play (onPlay mode).
  // hasEverPlayedRef предотвращает запуск сервиса когда аудио загрузилось но ещё не играет.
  useEffect(()=>{
    if(dur<=0)return;
    if(playing)hasEverPlayedRef.current=true;
    if(!hasEverPlayedRef.current)return;
    // Заиграл плеер — уведомление теперь про него (а не про фоновое аудио).
    if(playing)ms.claim();
    ms.setPlaybackState(playing?"playing":"paused");
  },[playing,dur]);
  // Позиция обновляется часто (при каждом timeupdate), поэтому отдельный эффект.
  useEffect(()=>{
    if(dur>0){
      ms.setPositionState({duration:dur,playbackRate:speed,position:Math.min(cur,dur)});
    }
  },[cur,dur,speed]);

  // Revoke последний artwork Blob URL при анмаунте компонента
  useEffect(()=>()=>{if(artworkBlobUrlRef.current)URL.revokeObjectURL(artworkBlobUrlRef.current);},[]);

  const peaksRef=useRef(null); // store last computed peaks so we can redraw on theme change

  useEffect(()=>{
    if(!audioSrc)return;
    let cancelled=false;
    // Дорожка и (в «Аркаде») её «горячая» копия для сыгранной части — общий рисовальщик wave.js.
    const drawPixels=(peaks)=>{paintWave(waveRef.current,peaks,P);paintWave(waveHotRef.current,peaks,P,{hot:true,width:waveRef.current?.offsetWidth});};
    // Заглушка сразу, чтобы при первом открытии дорожка не была пустой.
    paintWave(waveRef.current,null,P,{fake:true});
    (async()=>{
      // Check IDB cache first — instant draw if cached
      const cached=await _idbWave.get(audioSrc);
      if(cancelled)return;
      if(cached){peaksRef.current=cached;drawPixels(cached);return;}
      // Delay so the audio element gets full bandwidth on first open
      await new Promise(res=>{const tid=setTimeout(res,6000);if(cancelled)clearTimeout(tid);});
      if(cancelled)return;
      try{
        // Форма волны требует скачать файл ещё раз целиком (у аудио-тега свои запросы)
        // и распаковать его в память. Для файла из сети — только до 15 МБ (иначе это
        // двойной трафик), для скачанного на устройство — до 60 МБ. Больше — остаётся
        // нарисованная заглушка: раньше часовой файл мог подвесить вкладку на телефоне.
        const fromNet=/^https?:/i.test(audioSrc)&&!audioSrc.startsWith(window.location.origin);
        const MAX=(fromNet?15:60)*1024*1024;
        const r=await fetch(audioSrc,{priority:"low"});if(cancelled||!r.ok)return;
        if((+r.headers.get("content-length")||0)>MAX){try{r.body?.cancel();}catch{}return;}
        const buf=await r.arrayBuffer();if(cancelled||buf.byteLength>MAX)return;
        // Для формы волны хватает 3 кГц: памяти в ~15 раз меньше, чем при полном качестве.
        const OAC=window.OfflineAudioContext||window.webkitOfflineAudioContext;
        let buffer=null;
        for(const rate of [3000,22050]){
          if(buffer||!OAC)break;
          try{buffer=await new OAC(1,1,rate).decodeAudioData(buf.slice(0));}catch{}
        }
        if(!buffer){
          const AC=window.AudioContext||window.webkitAudioContext;if(!AC)return;
          const ctx=new AC();
          buffer=await ctx.decodeAudioData(buf);ctx.close();
        }
        if(cancelled)return;
        const raw=buffer.getChannelData(0);
        // Берём ширину ПОСЛЕ декодирования — canvas точно отрисован к этому моменту
        const W=waveRef.current?.offsetWidth||640;
        const bs=Math.max(1,Math.floor(raw.length/W));
        const peaks=[];
        for(let i=0;i<W;i++){
          let pk=0;
          try{for(let j=0;j<bs;j++){const v=Math.abs(raw[i*bs+j]||0);if(v>pk)pk=v;}}catch{}
          peaks.push(pk);
        }
        await _idbWave.set(audioSrc,peaks); // persist for next visit
        peaksRef.current=peaks;
        if(!cancelled)drawPixels(peaks);
      }catch(e){console.warn("[Wave]",e);}
    })();
    return()=>{cancelled=true;};
  },[audioSrc]);

  // Redraw waveform when theme changes (color changes but audioSrc stays the same)
  useEffect(()=>{
    if(!peaksRef.current||!waveRef.current)return;
    paintWave(waveRef.current,peaksRef.current,P);
    paintWave(waveHotRef.current,peaksRef.current,P,{hot:true,width:waveRef.current.offsetWidth});
  },[theme]); // eslint-disable-line react-hooks/exhaustive-deps

  // Клип: если повтор уже идёт — снять его; иначе зациклить [сейчас − N с; сейчас].
  const toggleClip=useCallback(()=>{
    const a=audioRef.current;if(!a)return;
    if(abActiveRef.current){
      setAbActive(false);abActiveRef.current=false;setAbA(null);abARef.current=null;setAbB(null);abBRef.current=null;return;
    }
    const end=a.currentTime;const start=Math.max(0,end-clipLenRef.current);
    if(end-start<0.3)return;
    setAbA(start);abARef.current=start;setAbB(end);abBRef.current=end;setAbActive(true);abActiveRef.current=true;
    seekToRef.current?.(start);
    if(a.paused){a.play().catch(()=>{});setPlaying(true);}
  },[]);
  const holdSlow=useCallback((on)=>{
    const a=audioRef.current;if(!a||slowHeldRef.current===on)return;
    slowHeldRef.current=on;setSlowHeld(on);
    a.playbackRate=on?slowRateRef.current:speedRef.current;
  },[]);

  useEffect(()=>{
    // Отпускание S (или потеря фокуса окна) возвращает обычную скорость.
    const onUp=(e)=>{if(e.code==="KeyS")holdSlow(false);};
    const onBlur=()=>holdSlow(false);
    window.addEventListener("keyup",onUp);window.addEventListener("blur",onBlur);
    return()=>{window.removeEventListener("keyup",onUp);window.removeEventListener("blur",onBlur);};
  },[holdSlow]);

  useEffect(()=>{
    const onKey=(e)=>{
      const tag=document.activeElement?.tagName;if(tag==="INPUT"||tag==="TEXTAREA"||!visible)return;
      // C / S по физической клавише (e.code) — работают и в русской раскладке.
      if(e.code==="KeyC"&&!e.ctrlKey&&!e.metaKey&&!e.altKey){e.preventDefault();toggleClip();return;}
      if(e.code==="KeyS"&&!e.ctrlKey&&!e.metaKey&&!e.altKey){e.preventDefault();if(!e.repeat)holdSlow(true);return;}
      if(e.key===" "){e.preventDefault();const a=audioRef.current;if(!a)return;if(playRef.current){a.pause();setPlaying(false);}else{a.play().catch(()=>{});setPlaying(true);}}
      if(e.key==="ArrowLeft"){e.preventDefault();seekToRef.current?.(audioRef.current?.currentTime-skipRef.current);}
      if(e.key==="ArrowRight"){e.preventDefault();seekToRef.current?.(audioRef.current?.currentTime+skipRef.current);}
      if(e.key==="z"&&(e.ctrlKey||e.metaKey)){e.preventDefault();performUndo();}
    };
    window.addEventListener("keydown",onKey);return()=>window.removeEventListener("keydown",onKey);
  },[visible,toggleClip,holdSlow]);

  useEffect(()=>{
    const a=audioRef.current;if(!a)return;
    const onMeta=()=>withFiniteDuration(a,setDur);
    const onEnded=()=>{if(!a.loop){setPlaying(false);onEndedRef.current?.();}};
    const onTime=()=>{
      if(a._acProbing)return;
      // Перемотали туда, где ещё не загружено: ползунок стоит на месте нажатия,
      // пока звук не догрузится (как в YouTube и Spotify), а не скачет.
      if(pendingSeekRef.current!=null){
        if(a.seeking||Math.abs(a.currentTime-pendingSeekRef.current)>1.5)return;
        pendingSeekRef.current=null;
      }
      const t=a.currentTime;setCur(t);
      // Если играет аудио другого проекта — не трогаем карточки этого проекта
      if(audioOwnerIdRef.current&&projectIdRef.current&&audioOwnerIdRef.current!==projectIdRef.current)return;
      // Запоминаем место раз в 5 секунд — следующее открытие продолжит отсюда.
      if(projectIdRef.current&&Math.abs(t-lastPosSaveRef.current)>=5){lastPosSaveRef.current=t;savePlayPos(projectIdRef.current,t,a.duration);}
      if(abActiveRef.current&&abARef.current!==null&&abBRef.current!==null&&t>=abBRef.current){
        setShownIds(p=>{const n=new Set(p);cardsRef.current.forEach(c=>{if(c.time_sec>=abARef.current&&c.time_sec<abBRef.current)n.delete(c.id);});return n;});
        prevT.current=abARef.current-0.01; // reset so card re-fires on next loop pass
        a.currentTime=abARef.current;return;
      }
      const hit=cardsRef.current.find(c=>!shownIds.has(c.id)&&prevT.current<c.time_sec&&t>=c.time_sec);
      if(hit){
        setActiveCard(hit);setFlipped(false);setPausedByFlip(false);setCardAnim(false);requestAnimationFrame(()=>setCardAnim(true));
        setShownIds(p=>new Set([...p,hit.id]));
        onCardChange&&onCardChange(hit);
        // Уведомление обновляется автоматически через useEffect([activeCard])
        if(pauseOnCard){const d=pauseDelayRef.current||0;if(d>0)setTimeout(()=>{if(audioRef.current){audioRef.current.pause();setPlaying(false);}},d*1000);else{a.pause();setPlaying(false);}}
      }
      prevT.current=t;
    };
    a.addEventListener("loadedmetadata",onMeta);a.addEventListener("ended",onEnded);a.addEventListener("timeupdate",onTime);
    return()=>{a.removeEventListener("loadedmetadata",onMeta);a.removeEventListener("ended",onEnded);a.removeEventListener("timeupdate",onTime);};
  },[shownIds,pauseOnCard]);

  // Загрузка: «крутилка» на кнопке, пока звук догружается, и полоса загруженного.
  const [buffering,setBuffering]=useState(false);
  const [buffered,setBuffered]=useState([]); // [[начало%, конец%], …]
  useEffect(()=>{
    const a=audioRef.current;if(!a)return;
    const wait=()=>setBuffering(true);
    const go=()=>setBuffering(false);
    const onSeeked=()=>{setBuffering(false);if(pendingSeekRef.current!=null&&Math.abs(a.currentTime-pendingSeekRef.current)<=1.5){pendingSeekRef.current=null;setCur(a.currentTime);}};
    const onProgress=()=>{
      const d=a.duration;if(!d||!Number.isFinite(d))return;
      const out=[];for(let i=0;i<a.buffered.length;i++)out.push([a.buffered.start(i)/d*100,a.buffered.end(i)/d*100]);
      setBuffered(prev=>prev.length===out.length&&prev.every((r,i)=>Math.abs(r[0]-out[i][0])<0.5&&Math.abs(r[1]-out[i][1])<0.5)?prev:out);
    };
    a.addEventListener("waiting",wait);a.addEventListener("seeking",wait);
    a.addEventListener("playing",go);a.addEventListener("canplay",go);a.addEventListener("seeked",onSeeked);a.addEventListener("pause",go);
    a.addEventListener("progress",onProgress);a.addEventListener("loadedmetadata",onProgress);a.addEventListener("durationchange",onProgress);
    return()=>{
      a.removeEventListener("waiting",wait);a.removeEventListener("seeking",wait);
      a.removeEventListener("playing",go);a.removeEventListener("canplay",go);a.removeEventListener("seeked",onSeeked);a.removeEventListener("pause",go);
      a.removeEventListener("progress",onProgress);a.removeEventListener("loadedmetadata",onProgress);a.removeEventListener("durationchange",onProgress);
    };
  },[audioSrc]);

  // Вдумчивый режим: считается отдельным каналом и идёт к уровневой цели.
  // Обычное слушание всегда пишется в `listen` (для общего объёма/стрика).
  const [focusedMode,setFocusedMode]=useState(()=>{
    try{return localStorage.getItem("ac_focused_mode")==="1";}catch{return false;}
  });
  const toggleFocused=useCallback(()=>{
    setFocusedMode(v=>{const n=!v;try{localStorage.setItem("ac_focused_mode",n?"1":"0");markSetting("ac_focused_mode");}catch{};return n;});
  },[]);
  // Listen-time tracking: record 1 second per second while playing.
  // Язык: явный src_lang проекта → активный язык → пусто (unattributed).
  useEffect(()=>{
    if(!playing)return;
    const projLang=resolveLang(project?.src_lang);
    const id=setInterval(()=>{
      recordTime("listen",1,projLang);
      if(focusedMode)recordTime("listen_focused",1,projLang);
    },1000);
    return()=>clearInterval(id);
  },[playing,project?.src_lang,focusedMode]);

  const isFirst=useRef(true);
  useEffect(()=>{
    if(isFirst.current){isFirst.current=false;return;}
    const cur=cardsRef.current;
    const ids=new Set(cur.map(c=>c.id));
    const removedIds=[...savedIdsRef.current].filter(id=>!ids.has(id));
    savedIdsRef.current=ids;
    const bulk=bulkDeleteRef.current;bulkDeleteRef.current=false;
    onSave(cur,nameRef.current,{projectId:projectIdRef.current,removedIds,bulk});
  },[cards]);

  // Undo stack
  const undoStackRef=useRef([]);
  const pushUndo=(action)=>{undoStackRef.current=[...undoStackRef.current.slice(-29),action];};
  const performUndo=useCallback(()=>{
    const action=undoStackRef.current[undoStackRef.current.length-1];if(!action)return;
    undoStackRef.current=undoStackRef.current.slice(0,-1);
    if(action.type==="deleteCard")setCards(prev=>[...prev,action.card]);
    if(action.type==="deleteCards")setCards(prev=>[...prev,...action.cards]);
  },[]);

  // Cards list ref for auto-scroll
  const cardsListRef=useRef(null);

  // Current playing card (last card whose time_sec <= cur)
  const currentCardId=useMemo(()=>{
    if(!cur)return null;
    const active=[...cards].sort((a,b)=>a.time_sec-b.time_sec).filter(c=>c.time_sec<=cur);
    return active.length?active[active.length-1].id:null;
  },[cur,cards]);

  // Manual scroll-to-current-card + auto-follow mode
  const scrollToCurrentCard=useCallback(()=>{
    if(autoScroll){setAutoScroll(false);return;} // повторное нажатие — выключаем
    setAutoScroll(true);
    if(!cardsListRef.current)return;
    const id=cardsRef.current?[...cardsRef.current].sort((a,b)=>a.time_sec-b.time_sec).filter(c=>c.time_sec<=(audioRef.current?.currentTime||0)).at(-1)?.id:null;
    const el=id?cardsListRef.current.querySelector(`[data-card-id="${id}"]`):null;
    if(el){isProgramScroll.current=true;el.scrollIntoView({behavior:"smooth",block:"nearest"});setTimeout(()=>{isProgramScroll.current=false;},1200);}
  },[autoScroll]);

  // Auto-scroll when currentCardId changes (only if autoScroll is enabled)
  useEffect(()=>{
    if(!autoScroll||!currentCardId||!cardsListRef.current)return;
    const el=cardsListRef.current.querySelector(`[data-card-id="${currentCardId}"]`);
    if(el){isProgramScroll.current=true;el.scrollIntoView({behavior:"smooth",block:"nearest"});setTimeout(()=>{isProgramScroll.current=false;},1200);}
  },[currentCardId,autoScroll]);
  const doSave=useCallback(()=>onSave(cardsRef.current,nameRef.current,{projectId:projectIdRef.current,removedIds:[]}),[onSave]);
  const togglePlay=()=>{
    const a=audioRef.current;if(!a)return;
    if(playing){a.pause();setPlaying(false);}
    else{
      onBeforePlay?.();
      // If user manually presses play while card is flipped, release flip-pause lock
      if(flipped)setPausedByFlip(false);
      a.play().catch(()=>{});
      setPlaying(true);
    }
  };

  // Flip the float card: pauses audio when showing translation, resumes when back
  const handleCardFlip=useCallback(()=>{
    setFlipped(prev=>{
      const newFlipped=!prev;
      const a=audioRef.current;
      if(a){
        if(newFlipped){
          if(playRef.current){a.pause();setPlaying(false);setPausedByFlip(true);}
        } else {
          setPausedByFlip(pb=>{if(pb){a.play().catch(()=>{});setPlaying(true);}return false;});
        }
      }
      return newFlipped;
    });
  },[]);

  const pinCard=useCallback((card)=>{
    setPinnedCards(prev=>{
      const idx=prev.length;
      // Position in top-right area, staggered
      return[...prev,{uid:Date.now(),card,x:Math.max(10,window.innerWidth-290-idx*22),y:Math.max(80,120+idx*22),w:260,flipped:false}];
    });
  },[]);
  const unpinCard=useCallback((uid)=>setPinnedCards(prev=>prev.filter(p=>p.uid!==uid)),[]);
  const togglePinFlip=useCallback((uid)=>setPinnedCards(prev=>prev.map(p=>p.uid===uid?{...p,flipped:!p.flipped}:p)),[]);

  const seekTo=useCallback((t)=>{
    const a=audioRef.current;if(!a)return;const d=a.duration;if(!d||isNaN(d))return;
    const cl=Math.max(0,Math.min(d,t));pendingSeekRef.current=cl;a.currentTime=cl;prevT.current=cl;setCur(cl);
    setShownIds(p=>{const n=new Set();p.forEach(id=>{const c=cardsRef.current.find(x=>x.id===id);if(c&&c.time_sec<cl)n.add(id);});return n;});
  },[]);
  useEffect(()=>{seekToRef.current=seekTo;},[seekTo]);

  const loopCard=useCallback((card)=>{
    if(abActiveRef.current&&abARef.current===card.time_sec){
      setAbA(null);setAbB(null);setAbActive(false);return;
    }
    const sorted=[...cardsRef.current].sort((a,b)=>a.time_sec-b.time_sec);
    const idx=sorted.findIndex(c=>c.id===card.id);
    const a=card.time_sec;
    const b=idx>=0&&idx<sorted.length-1?sorted[idx+1].time_sec:card.time_sec+5;
    setAbA(a);setAbB(b);setAbActive(true);seekToRef.current?.(a);
    const au=audioRef.current;if(au&&au.paused){au.play().catch(()=>{});setPlaying(true);}
  },[]);

  const jumpCard=(dir)=>{
    if(!cards.length)return;
    const sorted=[...cards].sort((a,b)=>a.time_sec-b.time_sec);
    const showCard=(c)=>{setActiveCard(c);setFlipped(false);setPausedByFlip(false);setCardAnim(false);requestAnimationFrame(()=>setCardAnim(true));setShownIds(p=>new Set([...p,c.id]));};
    if(dir==="prev"){
      const NEAR=2;const i=sorted.findIndex(c=>Math.abs(c.time_sec-cur)<NEAR);
      if(i>0){seekTo(sorted[i-1].time_sec);showCard(sorted[i-1]);return;}
      if(i===0){seekTo(0);return;}
      const past=sorted.filter(c=>c.time_sec<cur-NEAR);
      const target=past.length>0?past[past.length-1]:sorted[0];
      seekTo(target.time_sec);showCard(target);
    }else{
      const next=sorted.find(c=>c.time_sec>cur+0.5);
      if(next){seekTo(next.time_sec);showCard(next);}
    }
  };
  // ── Всегда актуальная ссылка — обновляем при каждом рендере ──────────────
  jumpCardRef.current=jumpCard;

  const ratioAt=(x)=>{const r=tlRef.current.getBoundingClientRect();return Math.max(0,Math.min(1,(x-r.left)/r.width));};
  const onTLMouseDown=(e)=>{if(!dur||e.button!==0)return;dragRef.current=true;seekTo(ratioAt(e.clientX)*dur);e.preventDefault();};
  const onTLRightClick=(e)=>{e.preventDefault();if(!dur)return;const t=ratioAt(e.clientX)*dur;const near=cards.find(c=>Math.abs(c.time_sec-t)<Math.max(dur*.018,1));near?openEdit(near):openNew(t);};
  useEffect(()=>{
    const mv=(e)=>{
      if(!tlRef.current)return;
      const t=Math.max(0,Math.min(dur,ratioAt(e.clientX)*dur));
      if(abDragRef.current==='A'){setAbA(abBRef.current!==null?Math.min(t,abBRef.current-0.05):t);}
      else if(abDragRef.current==='B'){setAbB(abARef.current!==null?Math.max(t,abARef.current+0.05):t);}
      else if(dragRef.current){seekTo(t);}
    };
    const up=()=>{dragRef.current=false;abDragRef.current=null;};
    window.addEventListener("mousemove",mv);window.addEventListener("mouseup",up);
    return()=>{window.removeEventListener("mousemove",mv);window.removeEventListener("mouseup",up);};
  },[dur]);
  const onTLTouchStart=(e)=>{const t=e.touches[0];longTimer.current=setTimeout(()=>{if(!dur)return;openNew(ratioAt(t.clientX)*dur);dragRef.current=false;},600);dragRef.current=true;if(dur)seekTo(ratioAt(t.clientX)*dur);};
  const onTLTouchMove=(e)=>{
    clearTimeout(longTimer.current);
    if(!tlRef.current)return;
    e.preventDefault();
    const t=Math.max(0,Math.min(dur,ratioAt(e.touches[0].clientX)*dur));
    if(abDragRef.current==='A'){setAbA(abBRef.current!==null?Math.min(t,abBRef.current-0.05):t);}
    else if(abDragRef.current==='B'){setAbB(abARef.current!==null?Math.max(t,abARef.current+0.05):t);}
    else if(dragRef.current){seekTo(t);}
  };
  const onTLTouchEnd=()=>{clearTimeout(longTimer.current);dragRef.current=false;abDragRef.current=null;};

  const openEdit=(c)=>{setEditing({...c});setTcInput(fmt(c.time_sec));};
  const openNew=(t)=>{const a=audioRef.current;if(a&&playRef.current){a.pause();setPlaying(false);}setEditing({id:null,time_sec:t,word:"",translation:""});setTcInput(fmt(t));};
  const saveCard=()=>{
    const t=parseTC(tcInput);const card={...editing,time_sec:!isNaN(t)&&t>=0?Math.min(t,dur||99999):editing.time_sec};
    if(!card.word.trim()){setEditing(null);return;}
    if(card.id)setCards(p=>p.map(c=>c.id===card.id?card:c));
    else setCards(p=>[...p,{...card,id:Date.now(),color:CARD_COLORS[p.length%CARD_COLORS.length]}]);
    setEditing(null);
  };
  const delCard=()=>{if(editing.id)setCards(p=>p.filter(c=>c.id!==editing.id));setEditing(null);};

  const handleSrtImport=(e)=>{
    const file=e.target.files[0];if(!file)return;
    const reader=new FileReader();
    reader.onload=async(ev)=>{
      const parsed=parseSubtitles(ev.target.result);
      if(!parsed.length){notify(t("Не удалось найти субтитры в файле."),{tone:"error"});return;}
      let added=0,skipped=0;
      const audioDur=audioRef.current?.duration||0;
      // Аудио — часть разрезанного файла, а субтитры от целого: спрашиваем, с какого
      // места начинается эта часть, и сдвигаем таймкоды (#208).
      const beyond=audioDur>0?parsed.filter(p=>p.timeSec>audioDur).length:0;
      let offset=0;
      if(beyond>0){
        const v=await askText({title:t("Сдвинуть субтитры?"),body:t("{0} из {1} строк позже конца этого аудио. Если это часть разрезанного файла — с какого времени она начинается? Например 30:00. Пусто — без сдвига.",[beyond,parsed.length]),placeholder:"30:00",okLabel:t("Импортировать")});
        if(v===null)return;
        offset=v.trim()?(parseTC(v.trim())||0):0;
      }
      setCards(prev=>{
        const next=[...prev];
        for(let i=0;i<parsed.length;i++){
          const{word,translation}=parsed[i];
          const timeSec=parsed[i].timeSec-offset;
          // Пропускаем карточки за пределами аудио
          if(timeSec<0||(audioDur>0&&timeSec>audioDur)){skipped++;continue;}
          if(!next.find(c=>Math.abs(c.time_sec-timeSec)<0.5&&c.word===word)){
            next.push({id:Date.now()+added,time_sec:timeSec,word,translation,color:CARD_COLORS[next.length%CARD_COLORS.length]});
            added++;
          }
        }
        return next;
      });
      const msg=skipped>0?t("Импортировано {0}. Пропущено {1} (за пределами аудио).",[nCards(added),skipped]):t("Импортировано {0}!",[nCards(added)]);
      notify(msg,{tone:"success"});
    };
    reader.readAsText(file,"utf-8");e.target.value="";
  };

  const handleTextImport=(parsed)=>{
    let added=0;
    setCards(prev=>{
      const next=[...prev];
      for(let i=0;i<parsed.length;i++){
        const{word,translation,time}=parsed[i];
        const t=time!=null?time:cur+added*0.1;
        next.push({id:Date.now()+added,time_sec:t,word,translation,color:CARD_COLORS[next.length%CARD_COLORS.length]});
        added++;
      }
      return next;
    });
    setShowTextImport(false);
    notify(t("Импортировано {0}!",[nCards(added)]),{tone:"success"});
  };

  const doSetA=()=>{setAbA(cur);if(abB!==null&&cur>=abB)setAbB(null);};
  // Поставили B после A — повтор начинается сразу (как в обычных плеерах); «AB» его выключает.
  const doSetB=()=>{if(abA===null||cur>abA){setAbB(cur);if(abA!==null)setAbActive(true);}};
  const clearAB=()=>{setAbA(null);setAbB(null);setAbActive(false);};

  const prog=dur?(cur/dur)*100:0;
  // Круглая кнопка на всплывающей карточке (цикл, правка, закрепить, закрыть).
  const cardBtn=(over)=>({width:32,height:32,borderRadius:"50%",display:"inline-flex",alignItems:"center",justifyContent:"center",padding:0,cursor:"pointer",flexShrink:0,
    background:P.lift?P.surface:"none",border:`1px solid ${P.border}`,color:P.mid,...over});
  const abAP=dur&&abA!==null?(abA/dur)*100:null;
  const abBP=dur&&abB!==null?(abB/dur)*100:null;
  const sorted=[...cards].sort((a,b)=>a.time_sec-b.time_sec);

  if(!project)return<audio ref={audioRef} style={{display:"none"}}/>;

  const STB=({active,onClick,children})=>(<Chip C={P} selected={active} onClick={onClick}>{children}</Chip>);

  // Детектируем: открыт другой проект пока играет старое аудио
  // Если project.audio_path задан, но текущий audioSrc его не содержит — показываем баннер
  // Чужое аудио: в плеере открыт проект B, но играет аудио проекта A
  const isAlienAudio=!!(audioOwnerId&&project?.id&&audioOwnerId!==project.id&&audioSrc&&dur>0);

  return(
    <div style={{display:visible?"block":"none"}}>
      <div style={{minHeight:"100vh",fontFamily:SANS,color:P.text,background:P.backdrop,position:"relative"}}>
        
        {/* onError: если плеер не смог загрузить источник (локальный файл побит,
            URL дохлый, сеть упала), помечаем что надо перетянуть со стрима.
            Раньше при corrupt local file Audio просто молчал, юзер видел
            «загружается» и ничего не происходило. */}
        <audio ref={audioRef} onError={(e)=>{
          const err=e?.currentTarget?.error;
          const code=err?.code; // 1=ABORTED 2=NETWORK 3=DECODE 4=SRC_NOT_SUPPORTED
          console.warn('[Audio] playback error code=',code,'src=',audioSrc?.slice(0,80));
          if(!project)return;
          // Если играли локальный файл (file:// или http://localhost/_capacitor_*)
          // и упало → файл побит или удалён системой. Триггерим повторный
          // _loadAudioForProj с очищенным local_audio_filename — он не найдёт
          // локальный файл и фолбэкнется на public URL + ensureAudioDownloaded
          // заново скачает в фоне.
          const isLocalSrc=audioSrc&&(audioSrc.startsWith('file:')||audioSrc.includes('_capacitor_'));
          if(isLocalSrc&&project.local_audio_filename){
            onLoadAudio?.({...project,local_audio_filename:null});
          }
        }}/>
        {/* Header: «←», название (или поле переименования), действия справа.
            Название сжимается, а не выталкивает кнопки: всё лишнее — в «…». */}
        <div style={{background:P.glassHdr,backdropFilter:"blur(14px)",WebkitBackdropFilter:"blur(14px)",borderBottom:`1px solid ${P.border}`,padding:"8px 12px",display:"flex",alignItems:"center",gap:8,position:"sticky",top:0,zIndex:10,minHeight:56,boxSizing:"border-box"}}>
          <button data-btn="" onClick={onBack} aria-label={t("Назад")} title={t("Назад")} className="ac-ibtn"
            style={{width:40,height:40,flexShrink:0,display:"inline-flex",alignItems:"center",justifyContent:"center",background:P.lift?P.surface:"none",border:`1px solid ${P.border}`,borderRadius:8,color:P.mid,cursor:"pointer",padding:0}}>
            <Icon n="arrowLeft" s={18}/>
          </button>
          {renamingProj?(
            <div style={{display:"flex",alignItems:"center",gap:6,flex:"1 1 auto",minWidth:0}}>
              <input autoFocus value={nameVal} onChange={e=>setNameVal(e.target.value)} aria-label={t("Новое название")}
                onKeyDown={e=>{if(e.key==="Enter"){if(nameVal.trim())doSave();setRenamingProj(false);}if(e.key==="Escape")setRenamingProj(false);}}
                style={{flex:"1 1 auto",width:0,minWidth:0,height:40,fontFamily:SANS,fontSize:15,color:P.text,...fieldSurface(P),borderColor:P.blue,borderRadius:8,padding:"0 10px",outline:"none"}}/>
              <UiIconButton C={P} variant="filled" size={40} label={t("Сохранить")} onClick={()=>{if(nameVal.trim())doSave();setRenamingProj(false);}}><Icon n="check" s={16}/></UiIconButton>
              <button onClick={()=>setRenamingProj(false)} aria-label={t("Отмена")} className="ac-ibtn"
                style={{width:40,height:40,flexShrink:0,background:P.lift?P.surface:"none",border:`1px solid ${P.border}`,color:P.mid,borderRadius:8,cursor:"pointer",display:"inline-flex",alignItems:"center",justifyContent:"center"}}><Icon n="close" s={14}/></button>
            </div>
          ):(<>
            <button onClick={()=>setRenamingProj(true)} title={t("Переименовать")} className="ac-ibtn"
              style={{flex:"1 1 auto",minWidth:0,display:"flex",alignItems:"center",gap:6,height:40,padding:"0 8px",background:"none",border:"none",borderRadius:8,cursor:"pointer",fontFamily:SANS,color:P.text,textAlign:"left"}}>
              <span style={{fontSize:15,fontWeight:600,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",minWidth:0}}>{nameVal}</span>
              <span style={{color:P.faint,display:"inline-flex",flexShrink:0}}><Icon n="edit" s={14}/></span>
            </button>
            {cards.length>0&&(
              <button data-btn="" onClick={()=>onQuiz(project.id,true)} title={t("SRS повторение проекта")} aria-label={t("SRS повторение проекта")} className="ac-ibtn"
                style={{width:40,height:40,flexShrink:0,display:"inline-flex",alignItems:"center",justifyContent:"center",background:P.lift?P.surface:"none",border:`1px solid ${P.border}`,borderRadius:8,color:P.mid,cursor:"pointer",padding:0}}><Icon n="cards" s={17}/></button>
            )}
            {onOpenQueue&&(
              <button data-btn="" onClick={onOpenQueue} title={queueCount?t("Очередь: {0}",[queueCount]):t("Очередь")} aria-label={queueCount?t("Очередь: {0}",[queueCount]):t("Очередь")} className="ac-ibtn"
                style={{position:"relative",width:40,height:40,flexShrink:0,display:"inline-flex",alignItems:"center",justifyContent:"center",background:P.lift?P.surface:"none",border:`1px solid ${P.border}`,borderRadius:8,color:queueCount?P.blueDark:P.mid,cursor:"pointer",padding:0}}>
                <Icon n="list" s={17}/>
                {queueCount>0&&<span aria-hidden="true" style={{position:"absolute",top:-6,right:-6,minWidth:18,height:18,padding:"0 5px",boxSizing:"border-box",borderRadius:9,background:P.blue,color:"#fff",fontSize:11,fontWeight:700,lineHeight:"18px",textAlign:"center",fontVariantNumeric:"tabular-nums"}}>{queueCount}</span>}
              </button>
            )}
            <button onClick={toggleFocused} aria-pressed={focusedMode}
              title={focusedMode?t("Вдумчивый режим включён — слушание идёт в зачёт уровня"):t("Включить вдумчивый режим: активное прослушивание считается отдельно и продвигает уровень")}
              aria-label={t("Вдумчивый режим")}
              style={{height:40,flexShrink:0,display:"inline-flex",alignItems:"center",gap:6,padding:wideLayout?"0 12px":"0 10px",borderRadius:8,cursor:"pointer",fontFamily:SANS,fontSize:13,fontWeight:600,
                background:focusedMode?P.bluePale:"transparent",color:focusedMode?P.blueDark:P.mid,border:`1px solid ${focusedMode?"transparent":P.border}`}}>
              <Icon n="target" s={16}/>{wideLayout&&<span>{t("Вдумчиво")}</span>}
            </button>
          </>)}
        </div>

        <div style={{maxWidth:wideLayout?1280:560,margin:"0 auto",padding:wideLayout?"0 28px 0":"0 14px 0",paddingBottom:wideLayout?"calc(40px + env(safe-area-inset-bottom, 16px))":"calc(80px + env(safe-area-inset-bottom, 20px))",position:"relative",zIndex:1,display:wideLayout?"grid":"block",gridTemplateColumns:wideLayout?"minmax(0,520px) 1fr":"none",gap:wideLayout?32:0,alignItems:"start"}}>
          {/* Left column: player controls */}
          <div style={{position:wideLayout?"sticky":"static",top:wideLayout?72:0}}>
          {/* Название */}
          <div style={{textAlign:"center",padding:wideLayout?"22px 0 16px":"10px 0 6px"}}>
            <div style={{fontFamily:FONT,fontSize:wideLayout?22:18,color:P.text,lineHeight:1.3}}>{nameVal}</div>
            {wideLayout&&<div style={{fontSize:11,color:P.faint,marginTop:4,textTransform:"uppercase",letterSpacing:".1em"}}>{t("Сейчас играет")}</div>}
          </div>

          {/* Таймлайн */}
          <div style={{marginBottom:wideLayout?14:8}}>
            <div style={{display:"flex",justifyContent:"space-between",marginBottom:5}}>
              <span style={{fontSize:12.5,color:P.mid,fontVariantNumeric:"tabular-nums"}}>{fmt(cur)}</span>
              <span style={{fontSize:12.5,color:P.mid,fontVariantNumeric:"tabular-nums"}}>{fmt(dur)}</span>
            </div>
            <div style={{position:"relative"}}>
              {/* Флажки над waveform. Это подсказка, где в аудио карточки; полноразмерные кнопки —
                  в списке карточек, поэтому флажкам можно быть мелкими и стоять вплотную
                  (data-layout-allow для автопроверки). overflow не режем — иначе не видно
                  всплывающее слово над флажком. */}
              {/* Флажок стоит ровно над своим местом в аудио (без прежнего «не ближе 1% к краю»,
                  из-за которого крайние сдвигались; отступ 1 px — как рамка дорожки). Флажок —
                  тонкая черта с кружком; где карточки идут тесно (ближе 9 px), кружок прячется —
                  аккуратные черты вместо налезающих квадратиков. */}
              <div data-layout-allow="small cover" style={{position:"relative",height:20,marginBottom:0}}>
                {dur>0&&(()=>{
                  const W=tlRef.current?.offsetWidth||600,gap=9/Math.max(1,W-2);
                  const sorted=[...cards].filter(c=>c.time_sec!=null).sort((x,y)=>x.time_sec-y.time_sec);
                  return sorted.map((c,i)=>{
                    const f=Math.min(1,Math.max(0,c.time_sec/dur));
                    const near=(j)=>j>=0&&j<sorted.length&&Math.abs(sorted[j].time_sec/dur-f)<gap;
                    const dense=near(i-1)||near(i+1);
                    const isHov=hovPin===c.id||touchPin===c.id,col=cardColorIn(P,c.color);
                    return(
                      <div key={c.id} onClick={()=>openEdit(c)}
                        onMouseEnter={()=>setHovPin(c.id)} onMouseLeave={()=>setHovPin(null)}
                        onTouchStart={()=>{clearTimeout(touchPinTimer.current);setTouchPin(c.id);touchPinTimer.current=setTimeout(()=>setTouchPin(null),2000);}}
                        style={{position:"absolute",left:`calc(1px + (100% - 2px) * ${f})`,top:0,bottom:0,width:12,transform:"translateX(-50%)",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"flex-end",cursor:"pointer",zIndex:isHov?9:8}}>
                        {isHov&&<div role="tooltip" style={{position:"absolute",bottom:"calc(100% + 4px)",left:"50%",transform:`translateX(${f<0.15?"-15%":f>0.85?"-85%":"-50%"})`,background:"#1e293b",color:"#f1f5f9",fontSize:11,fontWeight:600,padding:"3px 8px",borderRadius:12,whiteSpace:"nowrap",maxWidth:"60vw",overflow:"hidden",textOverflow:"ellipsis",pointerEvents:"none",zIndex:20,boxShadow:"0 2px 8px rgba(0,0,0,0.35)",border:"1px solid rgba(255,255,255,0.1)"}}>{c.word}</div>}
                        {(!dense||isHov)&&<div style={{width:isHov?8:6,height:isHov?8:6,borderRadius:"50%",background:col,boxShadow:`0 0 0 1.5px ${P.pageBg}`,marginBottom:-1,flexShrink:0,transition:"width .12s,height .12s"}}/>}
                        <div style={{width:isHov?2:1.5,height:dense&&!isHov?14:10,background:col,borderRadius:1,opacity:isHov?1:.9}}/>
                      </div>
                    );
                  });
                })()}
              </div>
              {/* Waveform */}
              <div ref={tlRef} style={{height:64,position:"relative",cursor:"ew-resize",userSelect:"none",touchAction:"none",borderRadius:9,background:P.surface,border:`1px solid ${P.border}`,overflow:"hidden"}}
                onMouseDown={onTLMouseDown} onMouseUp={()=>{dragRef.current=false;}} onContextMenu={onTLRightClick}
                onTouchStart={onTLTouchStart} onTouchMove={onTLTouchMove} onTouchEnd={onTLTouchEnd}>
                <canvas ref={waveRef} style={{position:"absolute",left:0,top:0,width:"100%",height:"100%",display:"block"}}/>
                {/* Сыгранная часть: подложка; при waveStyle 'eq' — та же дорожка ярко и со свечением
                    (обрезана по прогрессу) и белая светящаяся линия текущего места. */}
                {P.waveStyle==="eq"
                  ?<div style={{position:"absolute",left:0,top:0,width:`${prog}%`,height:"100%",overflow:"hidden",pointerEvents:"none",transition:"width .08s linear"}}>
                    <canvas ref={waveHotRef} style={{position:"absolute",left:0,top:0,height:"100%",display:"block"}}/>
                  </div>
                  :<div style={{position:"absolute",left:0,top:0,width:`${prog}%`,height:"100%",background:P.progressTint,pointerEvents:"none",transition:"width .08s linear"}}/>}
                {P.waveStyle==="eq"&&<div aria-hidden="true" style={{position:"absolute",left:`${prog}%`,top:0,bottom:0,width:2,marginLeft:-1,background:"#fff",boxShadow:"0 0 6px #fff, 0 0 14px #009dff",pointerEvents:"none",transition:"left .08s linear"}}/>}
                {/* Загруженная часть — полоска внизу (в незагруженное место перемотка дольше) */}
                {buffered.length>0&&!(buffered.length===1&&buffered[0][0]<0.5&&buffered[0][1]>99.5)&&buffered.map(([b0,b1],i)=>(
                  <div key={i} title={t("Загружено")} style={{position:"absolute",left:`${b0}%`,width:`${Math.max(0,b1-b0)}%`,bottom:0,height:3,background:P.faint,opacity:.55,pointerEvents:"none"}}/>
                ))}
                {abAP!==null&&abBP!==null&&<div style={{position:"absolute",left:`${abAP}%`,width:`${abBP-abAP}%`,top:0,height:"100%",background:"rgba(34,197,94,.15)",pointerEvents:"none"}}/>}
                {abAP!==null&&(
                  <div onMouseDown={e=>{e.stopPropagation();e.preventDefault();abDragRef.current='A';}} onTouchStart={e=>{e.stopPropagation();abDragRef.current='A';}}
                    style={{position:"absolute",left:`${abAP}%`,top:0,height:"100%",width:20,transform:"translateX(-50%)",cursor:"ew-resize",zIndex:15,display:"flex",alignItems:"center",justifyContent:"center"}}>
                    <div style={{width:3,height:"100%",background:"#22c55e",position:"relative",pointerEvents:"none"}}>
                      <div style={{position:"absolute",top:2,left:3,fontSize:9,color:"#22c55e",fontWeight:700,userSelect:"none"}}>A</div>
                      <div style={{position:"absolute",top:"50%",left:"50%",transform:"translate(-50%,-50%)",width:9,height:9,borderRadius:"50%",background:"#22c55e",border:"2px solid #fff",pointerEvents:"none"}}/>
                    </div>
                  </div>
                )}
                {abBP!==null&&(
                  <div onMouseDown={e=>{e.stopPropagation();e.preventDefault();abDragRef.current='B';}} onTouchStart={e=>{e.stopPropagation();abDragRef.current='B';}}
                    style={{position:"absolute",left:`${abBP}%`,top:0,height:"100%",width:20,transform:"translateX(-50%)",cursor:"ew-resize",zIndex:15,display:"flex",alignItems:"center",justifyContent:"center"}}>
                    <div style={{width:3,height:"100%",background:"#22c55e",position:"relative",pointerEvents:"none"}}>
                      <div style={{position:"absolute",top:2,left:3,fontSize:9,color:"#22c55e",fontWeight:700,userSelect:"none"}}>B</div>
                      <div style={{position:"absolute",top:"50%",left:"50%",transform:"translate(-50%,-50%)",width:9,height:9,borderRadius:"50%",background:"#22c55e",border:"2px solid #fff",pointerEvents:"none"}}/>
                    </div>
                  </div>
                )}
                <div style={{position:"absolute",left:`${prog}%`,top:0,width:2,height:"100%",background:P.blue,opacity:.9,transform:"translateX(-50%)",zIndex:9,transition:"left .08s linear"}}/>
                <div style={{position:"absolute",left:`${prog}%`,top:"50%",width:14,height:14,borderRadius:"50%",background:P.white,border:`2.5px solid ${P.blue}`,transform:"translate(-50%,-50%)",zIndex:10,transition:"left .08s linear",boxShadow:`0 2px 6px ${P.shadowMd}`}}/>
              </div>
            </div>
          </div>

          {/* Транспорт */}
          <div className="safe-zone-h" style={{display:"flex",alignItems:"center",justifyContent:"center",gap:16,marginBottom:wideLayout?16:10}}>
            <CircleBtn onClick={()=>jumpCard("prev")} title={t("Предыдущая карточка")}><Icon n="prev" s={18}/></CircleBtn>
            <CircleBtn onClick={()=>seekTo(cur-skip)} title={t("−{0}с",[skip])} style={{fontSize:12,fontWeight:700,color:P.mid}}>−{skip}{t("с")}</CircleBtn>
            <CircleBtn onClick={togglePlay} large title={buffering&&playing?t("Загружается…"):playing?t("Пауза (пробел)"):t("Играть (пробел)")} style={{position:"relative"}}>
              <Icon n={playing?"pause":"play"} s={22} style={playing?undefined:{marginLeft:2}}/>
              {buffering&&playing&&<span aria-hidden="true" style={{position:"absolute",inset:-4,borderRadius:"50%",border:"3px solid transparent",borderTopColor:P.blue,borderRightColor:P.blue,animation:"spin .8s linear infinite"}}/>}
            </CircleBtn>
            <CircleBtn onClick={()=>seekTo(cur+skip)} title={t("+{0}с",[skip])} style={{fontSize:12,fontWeight:700,color:P.mid}}>+{skip}{t("с")}</CircleBtn>
            <CircleBtn onClick={()=>jumpCard("next")} title={t("Следующая карточка")}><Icon n="next" s={18}/></CircleBtn>
          </div>

          {/* Клип и замедление — разобрать трудное место: зациклить последние секунды и слушать медленно, пока держишь */}
          <div className="safe-zone-h" style={{display:"flex",justifyContent:"center",gap:8,marginBottom:wideLayout?14:8,flexWrap:"wrap"}}>
            <button className="ac-press" onClick={toggleClip} aria-pressed={abActive}
              title={abActive?t("Снять повтор (C)"):t("Зациклить последние {0} с (клавиша C)",[clipLen])}
              style={{display:"inline-flex",alignItems:"center",gap:6,height:34,padding:"0 14px",borderRadius:17,fontSize:13,fontWeight:600,fontFamily:SANS,cursor:"pointer",
                border:`1px solid ${abActive?P.blue:P.border}`,background:abActive?P.blue:P.surface,color:abActive?"#fff":P.mid}}>
              <Icon n={abActive?"close":"loop"} s={15}/>{abActive?t("Снять повтор"):t("Клип {0} с",[clipLen])}
            </button>
            <button className="ac-press" aria-pressed={slowHeld}
              onPointerDown={e=>{e.preventDefault();try{e.currentTarget.setPointerCapture(e.pointerId);}catch{}holdSlow(true);}}
              onPointerUp={()=>holdSlow(false)} onPointerCancel={()=>holdSlow(false)} onContextMenu={e=>e.preventDefault()}
              title={t("Держите, чтобы слушать на {0}× (клавиша S)",[slowRate])}
              style={{display:"inline-flex",alignItems:"center",gap:6,height:34,padding:"0 14px",borderRadius:17,fontSize:13,fontWeight:600,fontFamily:SANS,cursor:"pointer",
                touchAction:"none",userSelect:"none",WebkitUserSelect:"none",WebkitTouchCallout:"none",
                border:`1px solid ${slowHeld?P.warm:P.border}`,background:slowHeld?P.warm:P.surface,color:slowHeld?"#fff":P.mid}}>
              <Icon n="gauge" s={15}/>{slowHeld?`${slowRate}×`:t("Держать: {0}×",[slowRate])}
            </button>
          </div>

          {/* Громкость */}
          <div className="safe-zone-h" style={{display:"flex",alignItems:"center",gap:10,marginBottom:wideLayout?14:8,padding:"0 4px"}}>
            <VolumeControl C={P} value={vol} muted={muted} width="fill" showPct
              onInput={(v)=>{const a=audioRef.current;if(a)a.volume=v;}} onChange={setVol} onMute={()=>setMuted(m=>!m)}/>
          </div>

          {/* + Карточку */}
          <div className="safe-zone-h" style={{display:"flex",justifyContent:"center",marginBottom:wideLayout?14:8}}>
            <UiButton C={P} variant="primary" size="sm" onClick={()=>openNew(cur)}>{t("+ карточку на")} {fmt(cur)}</UiButton>
          </div>

          {/* Настройки */}
          <div className="safe-zone-h" style={{marginBottom:wideLayout?14:8}}>
            <button data-btn="" onClick={()=>setShowSettings(v=>!v)} aria-expanded={showSettings} style={{width:"100%",background:P.surface,border:`1px solid ${P.border}`,color:P.mid,borderRadius:showSettings?"12px 12px 0 0":12,padding:"9px 14px",fontSize:13,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"space-between",fontFamily:SANS}}>
              <span style={{display:"flex",alignItems:"center",gap:8}}><Icon n="settings"/>{t("Настройки воспроизведения")}</span><Icon n={showSettings?"chevronUp":"chevronDown"}/>
            </button>
            {showSettings&&(
              <div style={{...panelSurface(P),borderTop:"none",borderRadius:"0 0 12px 12px",padding:"12px 14px"}}>
                <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:10,flexWrap:"wrap"}}>
                  <span style={{fontSize:11,color:P.mid,flexShrink:0}}>{t("Перемотка:")}</span>
                  {[2,5,10].map(s=><STB key={s} active={skip===s} onClick={()=>setSkip(s)}>{s}{t("с")}</STB>)}
                  <div style={{flex:1}}/>
                  <STB active={looping} onClick={()=>setLooping(l=>!l)}><Icon n="loop" s={14}/> {t("Цикл")}</STB>
                  <STB active={pauseOnCard} onClick={()=>setPauseOnCard(p=>!p)}><Icon n="pause" s={14}/> {t("Пауза")}</STB>
                </div>
                <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:pauseOnCard?10:0,position:"relative"}}>
                  <span style={{fontSize:11,color:P.mid,flexShrink:0}}>{t("Скорость:")}</span>
                  <div style={{position:"relative",flex:1}}>
                    <button data-btn="" onClick={()=>setShowSpeedPopup(v=>!v)} aria-haspopup="listbox" aria-expanded={showSpeedPopup}
                      style={{width:"100%",minHeight:36,background:P.surface,border:`1px solid ${P.border}`,color:P.text,borderRadius:9,padding:"6px 12px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:SANS,textAlign:"left",display:"flex",justifyContent:"space-between",alignItems:"center",gap:8}}>
                      <span style={{fontVariantNumeric:"tabular-nums"}}>{fmtSpeed(speed)}</span>
                      <span style={{fontSize:12.5,color:P.faint,fontWeight:500}}>{speedLabel(speed)} ▾</span>
                    </button>
                    {showSpeedPopup&&<SpeedPopup speed={speed} onSelect={s=>{setSpeed(s);}} onClose={()=>setShowSpeedPopup(false)}/>}
                  </div>
                </div>
                {pauseOnCard&&<div style={{display:"flex",alignItems:"center",gap:6,marginBottom:10,marginTop:10,flexWrap:"wrap"}}><span style={{fontSize:11,color:P.mid,flexShrink:0}}>{t("Задержка:")}</span>{[0,.3,.5,1,1.5].map(d=><STB key={d} active={pauseDelay===d} onClick={()=>setPauseDelay(d)}>{d}{t("с")}</STB>)}</div>}
                <div style={{display:"flex",alignItems:"center",gap:6,marginTop:10,flexWrap:"wrap"}}>
                  <span style={{fontSize:11,color:P.mid,flexShrink:0}}>A-B:</span>
                  <Chip C={P} onClick={doSetA} style={abA!==null?{background:P.green,borderColor:P.green,color:readableOn(P.green)}:undefined}>{abA!==null?`A: ${fmt(abA)}`:t("Поставить A")}</Chip>
                  <Chip C={P} onClick={doSetB} style={abB!==null?{background:P.green,borderColor:P.green,color:readableOn(P.green)}:undefined}>{abB!==null?`B: ${fmt(abB)}`:t("Поставить B")}</Chip>
                  {abA!==null&&abB!==null&&<><Chip C={P} icon="play" selected={abActive} title={abActive?t("Остановить повтор A–B"):t("Повторять отрезок A–B")} onClick={()=>setAbActive(a=>!a)} style={abActive?{background:P.green,borderColor:P.green,color:readableOn(P.green)}:undefined}>AB</Chip>
                    <UiIconButton C={P} icon="close" iconSize={14} size={32} variant="ghost" label={t("Убрать A-B")} onClick={clearAB}/></>}
                </div>
                {/* Клип и замедление */}
                <div style={{borderTop:`1px solid ${P.border}`,marginTop:12,paddingTop:12,display:"flex",flexDirection:"column",gap:10}}>
                  <label style={{display:"flex",alignItems:"center",gap:10,fontSize:12,color:P.mid}}>
                    <span style={{width:132,flexShrink:0}}>{t("Клип: последние")} <b style={{color:P.text}}>{clipLen} {t("с")}</b></span>
                    <Slider C={P} min={1} max={15} step={1} value={clipLen} onChange={e=>saveClipLen(parseFloat(e.target.value))}/>
                  </label>
                  <label style={{display:"flex",alignItems:"center",gap:10,fontSize:12,color:P.mid}}>
                    <span style={{width:132,flexShrink:0}}>{t("Замедление:")} <b style={{color:P.text}}>{slowRate}×</b></span>
                    <Slider C={P} min={0.3} max={0.95} step={0.05} value={slowRate} onChange={e=>saveSlowRate(Math.round(parseFloat(e.target.value)*100)/100)}/>
                  </label>
                  <div style={{fontSize:11,color:P.faint,lineHeight:1.45}}>
                    {t("Клавиши:")} <b>C</b> {t("— зациклить последние секунды (ещё раз — снять),")} <b>S</b> {t("(держать) — слушать медленно. Вместе: услышали непонятное место → C → держите S.")}
                  </div>
                </div>
                {/* ── Офлайн-копия ─────────────────────────────────────── */}
                {project?.audio_path&&(()=>{
                  const st=downloadStatus[project.id]??"idle";
                  const isDone=st==="done"||st===100;
                  const isProg=typeof st==="number"&&st>=0&&st<100;
                  const isErr=st==="error";
                  return(
                    <div style={{borderTop:`1px solid ${P.border}`,marginTop:12,paddingTop:12,display:"flex",alignItems:"center",justifyContent:"space-between",gap:8,flexWrap:"wrap"}}>
                      <div>
                        <div style={{fontSize:11,fontWeight:700,color:P.mid,textTransform:"uppercase",letterSpacing:".07em",marginBottom:2}}>{t("Офлайн-копия")}</div>
                        <div style={{fontSize:11,color:isDone?"#16a34a":isErr?"#dc2626":isProg?P.blue:P.faint}}>
                          {isDone?t("✓ Скачано"):isProg?t("Загрузка {0}%...",[st]):isErr?t("⚠ Ошибка"):t("Не скачано")}
                        </div>
                      </div>
                      <div style={{display:"flex",gap:6,flexShrink:0}}>
                        {isDone?(
                          <UiButton C={P} size="sm" danger icon="trash" onClick={()=>ask({title:t("Удалить офлайн-копию? Проект останется в списке."),confirmLabel:t("Удалить загрузку")},()=>onDeleteOffline?.(project))}>
                            {t("Удалить загрузку")}
                          </UiButton>
                        ):(
                          <UiButton C={P} size="sm" variant={isProg?"secondary":"primary"} icon={isProg||isErr?undefined:"download"} onClick={()=>onDownload?.(project)} disabled={isProg}>
                            {isProg?`${st}%...`:isErr?t("⟳ Повторить"):t("Скачать офлайн")}
                          </UiButton>
                        )}
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}
          </div>

          {/* Текст аудио (#188): полный текст прямо в проекте — свёрнут, пока не нажмёшь.
              Нет текста — можно вставить его здесь же (станет текстовым проектом, уже
              привязанным к этому аудио) или привязать готовый. */}
          <div className="safe-zone-h" style={{marginBottom:wideLayout?14:8}}>
            <button data-btn="" onClick={()=>setShowLinkedText(v=>!v)} aria-expanded={showLinkedText} style={{width:"100%",background:P.surface,border:`1px solid ${P.border}`,color:P.mid,borderRadius:showLinkedText?"12px 12px 0 0":12,padding:"9px 14px",fontSize:13,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"space-between",gap:8,fontFamily:SANS}}>
              <span style={{display:"flex",alignItems:"center",gap:8,minWidth:0}}>
                <Icon n="book"/>
                <span style={{overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{linkedTextProject?t("Текст · {0}",[linkedTextProject.name]):t("Текст")}</span>
                {linkedTextProject?.content&&<span style={{fontSize:11,color:P.faint,flexShrink:0}}>{nWords(countWords(linkedTextProject.content,linkedTextProject.src_lang))}</span>}
              </span>
              <Icon n={showLinkedText?"chevronUp":"chevronDown"}/>
            </button>
            {showLinkedText&&(
              <div style={{...panelSurface(P),borderTop:"none",borderRadius:"0 0 12px 12px",padding:"12px 14px"}}>
                {linkedTextProject?(
                  <>
                    <div style={{maxHeight:"min(50vh,440px)",overflowY:"auto",fontFamily:FONT,fontSize:15.5,lineHeight:1.75,color:P.text,whiteSpace:"pre-wrap",wordBreak:"break-word",paddingRight:4}}>
                      {linkedTextProject.content
                        ?(linkedTextProject.content.length>30000?linkedTextProject.content.slice(0,30000)+"…":linkedTextProject.content)
                        :<span style={{color:P.faint,fontFamily:SANS,fontSize:13}}>{t("Текст пустой — откройте его в читалке и добавьте.")}</span>}
                    </div>
                    <div style={{display:"flex",gap:6,flexWrap:"wrap",alignItems:"center",marginTop:10,paddingTop:10,borderTop:`1px solid ${P.borderSoft}`}}>
                      <button onClick={()=>onOpenTextProject?.(linkedTextProject)} title={t("Читалка: перевод слов по нажатию, карточки, озвучка")} style={{background:P.blue,border:`1.5px solid ${P.blue}`,color:"#fff",padding:"6px 12px",borderRadius:9,fontSize:12,fontWeight:500,cursor:"pointer",fontFamily:SANS,display:"inline-flex",alignItems:"center",gap:6}}><Icon n="book"/>{t("Открыть в читалке")}</button>
                      <button onClick={()=>ask({title:t("Отвязать текст от этого аудио? Сам текст останется в библиотеке."),confirmLabel:t("Отвязать"),danger:false},()=>onLinkTextToAudio?.(linkedTextProject.id,null))} style={{background:P.lift?P.surface:"none",border:`1px solid ${P.border}`,color:P.faint,padding:"6px 10px",borderRadius:9,fontSize:12,cursor:"pointer",fontFamily:SANS}}>{t("Отвязать")}</button>
                    </div>
                  </>
                ):(
                  <>
                    <textarea value={textDraft} onChange={e=>setTextDraft(e.target.value)} rows={5}
                      placeholder={t("Вставьте текст этого аудио: расшифровку, статью, субтитры…")}
                      style={{width:"100%",boxSizing:"border-box",background:P.surface,border:`1.5px solid ${P.border}`,borderRadius:10,color:P.text,padding:"10px 12px",fontSize:14,fontFamily:FONT,lineHeight:1.6,outline:"none",resize:"vertical",display:"block"}}/>
                    <div style={{display:"flex",alignItems:"center",gap:8,marginTop:8}}>
                      <button disabled={!textDraft.trim()||textSaving||!onCreateLinkedText} className="ac-press"
                        onClick={async()=>{setTextSaving(true);try{await onCreateLinkedText(project,textDraft);setTextDraft("");}finally{setTextSaving(false);}}}
                        style={{background:textDraft.trim()?P.blue:P.surface,border:`1.5px solid ${textDraft.trim()?P.blue:P.border}`,color:textDraft.trim()?"#fff":P.faint,padding:"7px 14px",borderRadius:9,fontSize:13,fontWeight:500,cursor:textDraft.trim()?"pointer":"default",fontFamily:SANS}}>
                        {textSaving?t("Сохраняю…"):t("Сохранить текст")}
                      </button>
                      {textDraft.trim()&&<span style={{fontSize:11,color:P.faint}}>{nWords(countWords(textDraft,project?.src_lang))}</span>}
                    </div>
                    {textProjects.filter(tp=>!tp.linked_audio_id).length>0&&(
                      <>
                        <div style={{fontSize:12,color:P.mid,margin:"14px 0 6px"}}>{t("или привязать готовый текст:")}</div>
                        <div style={{display:"flex",flexDirection:"column",gap:4,maxHeight:160,overflowY:"auto"}}>
                          {textProjects.filter(tp=>!tp.linked_audio_id).map(tp=>(
                            <button key={tp.id} onClick={()=>onLinkTextToAudio?.(tp.id,project?.id)} style={{background:P.surface,border:`1px solid ${P.border}`,color:P.text,padding:"7px 10px",borderRadius:9,fontSize:12.5,cursor:"pointer",textAlign:"left",fontFamily:SANS,display:"flex",alignItems:"center",gap:8}}><Icon n="link"/>{tp.name}</button>
                          ))}
                        </div>
                      </>
                    )}
                  </>
                )}
              </div>
            )}
          </div>

          {/* Плавающая карточка — всегда под настройками, можно перетащить */}
          {activeCard&&(
            <div style={{
              position:floatCardPos?"fixed":"relative",
              left:floatCardPos?floatCardPos.x:undefined,
              top:floatCardPos?floatCardPos.y:undefined,
              marginBottom:floatCardPos?0:16,
              zIndex:floatCardPos?200:1,
              width:floatCardPos?floatCardSize.w:"100%",
              height:floatCardSize.h||undefined,
              minHeight:110,
              maxWidth:floatCardPos?"92vw":"100%",
              background:P.glassCard,backdropFilter:"blur(14px)",
              border:`2px solid ${activeCard.color}`,borderRadius:9,
              boxShadow:`0 8px 36px ${P.shadowMd}`,overflow:"hidden",
              animation:cardAnim?"ac-fade-in .22s ease":"none"}}>
              {/* Drag handle bar */}
              <div onMouseDown={e=>{e.preventDefault();const r=e.currentTarget.parentElement.getBoundingClientRect();floatDragStartRef.current={smx:e.clientX,smy:e.clientY,sx:floatCardPos?floatCardPos.x:r.left,sy:floatCardPos?floatCardPos.y:r.top};}}
                onTouchStart={e=>{const t=e.touches[0];const r=e.currentTarget.parentElement.getBoundingClientRect();floatDragStartRef.current={smx:t.clientX,smy:t.clientY,sx:floatCardPos?floatCardPos.x:r.left,sy:floatCardPos?floatCardPos.y:r.top};}}
                style={{padding:"7px 14px 5px",display:"flex",justifyContent:"space-between",alignItems:"center",cursor:"grab",borderBottom:`1px solid ${activeCard.color}30`,gap:6}}>
                <span style={{fontSize:10,color:P.faint,textTransform:"uppercase",letterSpacing:".12em",fontWeight:600}}>{fmt(activeCard.time_sec)}</span>
                <div style={{display:"flex",gap:4,alignItems:"center"}}>
                  {/* Кнопки карточки — одинаковые кружки 32 px (раньше были разного размера и формы). */}
                  {floatCardPos&&<button onClick={()=>setFloatCardPos(null)} title={t("Вернуть на место")} style={{background:P.lift?P.surface:"none",border:`1px solid ${P.border}`,color:P.mid,fontSize:11,cursor:"pointer",minHeight:28,padding:"0 10px",borderRadius:14,fontFamily:SANS,flexShrink:0}}>{t("↩ на место")}</button>}
                  <button onClick={()=>loopCard(activeCard)} aria-label={abActive&&abA===activeCard?.time_sec?t("Остановить цикл"):t("Зациклить карточку")} title={abActive&&abA===activeCard?.time_sec?t("Остановить цикл"):t("Зациклить карточку")} style={cardBtn(abActive&&abA===activeCard?.time_sec?{background:P.blue,border:`1px solid ${P.blue}`,color:readableOn(P.blue)}:{})}>{IC.loop}</button>
                  <button onClick={()=>{const c=cards.find(x=>x.id===activeCard.id);if(c)openEdit(c);}} aria-label={t("Редактировать")} title={t("Редактировать")} style={cardBtn({})}>{IC.edit}</button>
                  <button onClick={()=>pinCard(activeCard)} aria-label={t("Закрепить карточку")} title={t("Закрепить карточку")} style={cardBtn({background:`${activeCard.color}20`,border:`1px solid ${activeCard.color}60`,color:activeCard.color})}>{IC.pin}</button>
                  <button onClick={()=>{setActiveCard(null);setFlipped(false);setPausedByFlip(false);}} aria-label={t("Закрыть")} title={t("Закрыть")} style={cardBtn({})}><Icon n="close" s={14}/></button>
                </div>
              </div>
              <div style={{overflowY:"auto",maxHeight:floatCardSize.h?floatCardSize.h-50:260,padding:"10px 14px 14px",scrollbarWidth:"none",msOverflowStyle:"none"}} className="no-scrollbar">
                {(()=>{
                  const cardH=floatCardSize.h||200;
                  const cardW=floatCardSize.w||340;
                  // ── Адаптивный шрифт по длине текста ───────────────────
                  const text=activeCard.word||"";
                  const textLen=text.length;
                  const dimFs=Math.min(cardW/6.5,cardH/3.2,62);
                  const lenScale=textLen<=12?1:textLen<=25?0.82:textLen<=50?0.65:textLen<=90?0.50:textLen<=150?0.40:0.33;
                  const wordFs=Math.max(13,Math.round(dimFs*lenScale));
                  const transFs=Math.max(12,Math.min(Math.round(cardW/13),Math.round(cardH/7),30));
                  return(
                    <div onClick={()=>handleCardFlip()} style={{cursor:"pointer",minHeight:cardH-80,display:"flex",flexDirection:"column",justifyContent:"center",alignItems:"flex-start"}}>
                    {!flipped?(
                      // ЛИЦЕВАЯ: только слово/фраза — перевод скрыт
                      <>
                        <div style={{fontFamily:FONT,fontSize:wordFs,color:activeCard.color,lineHeight:1.3,wordBreak:"break-word",overflowWrap:"break-word",width:"100%"}}>{text}</div>
                        <div style={{fontSize:10,color:P.faint,marginTop:6,opacity:0.6}}>{t("нажмите → перевод")}</div>
                      </>
                    ):(
                      <>
                        <div style={{fontSize:Math.max(11,Math.min(transFs*0.7,18)),color:P.mid,marginBottom:8,wordBreak:"break-word"}}>{activeCard.word}</div>
                        <div style={{height:1,background:P.borderSoft,marginBottom:10}}/>
                        <div style={{fontFamily:FONT,fontSize:transFs,color:P.text,fontWeight:700,lineHeight:1.3,wordBreak:"break-word"}}>{activeCard.translation||"—"}</div>
                      </>
                    )}
                    </div>
                  );
                })()}
              </div>
              {/* Resize SE corner */}
              <div onMouseDown={e=>{e.preventDefault();floatResizeRef.current={smx:e.clientX,smy:e.clientY,sw:floatCardPos?floatCardSize.w:e.currentTarget.parentElement.offsetWidth,sh:floatCardSize.h||e.currentTarget.parentElement.offsetHeight};}}
                onTouchStart={e=>{const t=e.touches[0];floatResizeRef.current={smx:t.clientX,smy:t.clientY,sw:floatCardPos?floatCardSize.w:e.currentTarget.parentElement.offsetWidth,sh:floatCardSize.h||e.currentTarget.parentElement.offsetHeight};}}
                style={{position:"absolute",bottom:4,right:4,width:14,height:14,cursor:"se-resize",borderRight:`2px solid ${P.border}`,borderBottom:`2px solid ${P.border}`,borderRadius:"0 0 4px 0",opacity:0.5}}/>
            </div>
          )}

          </div>{/* end left column */}

          {/* Right column: cards list */}
          <div style={{paddingTop:wideLayout?24:0}}>
          {/* Карточки — playlist */}
          <div className="cards-safe">
            <div className="cards-safe-toolbar" style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10,gap:6}}>
              <div style={{display:"flex",alignItems:"center",gap:8}}>
                <span style={{fontFamily:FONT,fontSize:16,color:P.text,whiteSpace:"nowrap"}}>{t("Карточки")} <span style={{fontSize:13,color:P.faint,fontFamily:SANS}}>{cards.length}</span></span>
                <IconBtn onClick={scrollToCurrentCard} title={autoScroll?t("Автоскролл вкл"):t("Перейти к текущей")} active={autoScroll}>{IC.scroll}</IconBtn>
              </div>
              <div style={{display:"flex",gap:4,alignItems:"center",flexWrap:"nowrap",justifyContent:"flex-end",flexShrink:0}}>
                {selectMode?(
                  <>
                    {(()=>{const all=sorted.length>0&&selectedCards.size===sorted.length;/* повторное «Все» снимает выделение */return <IconBtn onClick={()=>setSelectedCards(all?new Set():new Set(sorted.map(c=>c.id)))} active={all} title={all?t("Снять выделение"):t("Выбрать все")}>{IC.checkAll}</IconBtn>;})()}
                    <IconBtn onClick={()=>{
                      const sel=sorted.filter(c=>selectedCards.has(c.id));
                      if(!sel.length)return;
                      sel.sort((a,b)=>a.time_sec-b.time_sec);
                      const a=sel[0].time_sec;
                      const lastIdx=sorted.findIndex(c=>c.id===sel[sel.length-1].id);
                      const b=lastIdx>=0&&lastIdx<sorted.length-1?sorted[lastIdx+1].time_sec:sel[sel.length-1].time_sec+5;
                      setAbA(a);setAbB(b);setAbActive(true);seekTo(a);
                    }} title={t("Зациклить выбранные")}>{IC.loop}</IconBtn>
                    <IconBtn onClick={()=>{if(selectedCards.size>0)ask({title:t("Удалить {0}?",[nCards(selectedCards.size)]),confirmLabel:t("Удалить")},()=>{pushUndo({type:"deleteCards",cards:sorted.filter(c=>selectedCards.has(c.id))});bulkDeleteRef.current=true;setCards(p=>p.filter(c=>!selectedCards.has(c.id)));setSelectedCards(new Set());setSelectMode(false);});}} title={t("Удалить{0}",[selectedCards.size>0?` (${selectedCards.size})`:""])} danger><Icon n="trash"/></IconBtn>
                    <IconBtn onClick={()=>{setSelectMode(false);setSelectedCards(new Set());}} title={t("Отмена выбора")} aria-label={t("Отмена выбора")}><Icon n="close" s={14}/></IconBtn>
                  </>
                ):(
                  <>
                    <IconBtn onClick={performUndo} title={t("Отменить (Ctrl+Z)")}>{IC.undo}</IconBtn>
                    <IconBtn onClick={()=>setSelectMode(true)} title={t("Выбрать несколько")}>{IC.select}</IconBtn>
                    <IconBtn onClick={()=>srtRef.current?.click()} title={t("Импорт SRT/VTT")}>{IC.srt}</IconBtn>
                    <IconBtn onClick={()=>setShowTextImport(true)} title={t("Импорт текста")}>{IC.text}</IconBtn>
                  </>
                )}
              </div>
              <input ref={srtRef} type="file" accept=".srt,.vtt,.txt" style={{display:"none"}} onChange={handleSrtImport}/>
            </div>
            {sorted.length===0?(
              <div style={{...panelSurface(P),borderRadius:9,border:`1px dashed ${P.border}`,padding:"24px",textAlign:"center"}}><div style={{fontSize:22,marginBottom:6}}><Icon n="copy" s={22} sw={1.5}/></div><div style={{fontSize:13,color:P.faint}}>{t("ПКМ на таймлайне или кнопка выше")}</div></div>
            ):(
              <div ref={cardsListRef}
                className="cards-list"
                onScroll={()=>{if(!isProgramScroll.current){setAutoScroll(false);}}}
                data-panel="" style={{...panelSurface(P),borderRadius:9,overflow:"hidden",maxHeight:wideLayout?"calc(100vh - 160px)":"calc(100dvh - 250px)",overflowY:"auto"}}>
                {(rowFns.current={openEdit,pushUndo,seekTo,loopCard})&&sorted.map((c,i)=>{
                  const isSel=selectedCards.has(c.id);
                  return(
                    <CardRow key={c.id} c={c} i={i} total={sorted.length} pal={P}
                      selectMode={selectMode} isSel={isSel} isActive={c.id===currentCardId}
                      isLooped={abActive&&abA===c.time_sec}
                      onSelect={()=>setSelectedCards(p=>{const n=new Set(p);isSel?n.delete(c.id):n.add(c.id);return n;})}
                      onEdit={()=>rowFns.current.openEdit(c)}
                      onDelete={()=>{rowFns.current.pushUndo({type:"deleteCard",card:c});setCards(p=>p.filter(x=>x.id!==c.id));}}
                      onLongPress={()=>{setSelectMode(true);setSelectedCards(new Set([c.id]));}}
                      onSeek={(t)=>rowFns.current.seekTo(t)}
                      onLoop={(c)=>rowFns.current.loopCard(c)}                    />
                  );
                })}
              </div>
            )}
          </div>
          </div>{/* end right column */}
        </div>{/* end grid */}

        {confirmEl}
        {promptEl}
        {/* Окно карточки: новая или правка */}
        {editing&&(
          <Modal size="sm" onClose={()=>setEditing(null)} title={editing.id?t("Редактировать карточку"):t("Новая карточка")}
            footer={<>
              {editing.id&&<UiButton C={P} danger onClick={delCard} style={{marginRight:"auto"}}>{t("Удалить")}</UiButton>}
              <UiButton C={P} onClick={()=>setEditing(null)}>{t("Отмена")}</UiButton>
              <UiButton C={P} variant="primary" onClick={saveCard}>{t("Сохранить")}</UiButton>
            </>}>
            <div style={{display:"flex",gap:8,alignItems:"flex-end",marginBottom:14,flexWrap:"wrap"}}>
              <Field C={P} label={t("Время")} value={tcInput} onChange={e=>setTcInput(e.target.value)} placeholder="0:00" onKeyDown={e=>e.key==="Enter"&&saveCard()}
                style={{marginBottom:0,width:96,flex:"0 0 auto"}} inputStyle={{fontVariantNumeric:"tabular-nums"}}/>
              <UiButton C={P} size="md" onClick={()=>setTcInput(fmt(cur))} style={{minHeight:44}}>{t("Текущий ·")} {fmt(cur)}</UiButton>
            </div>
            <Field C={P} label={t("Слово или фраза")} value={editing.word||""} placeholder={t("напр. résoudre")} autoFocus
              onChange={e=>setEditing(p=>({...p,word:e.target.value}))} onKeyDown={e=>e.key==="Enter"&&saveCard()}/>
            {/* Перевод с подсказками (как в Quizlet): словарь → Claude → MyMemory, для японского — кана */}
            <CardTranslationField value={editing.translation} onChange={v=>setEditing(p=>({...p,translation:v}))} onEnter={saveCard}
              word={editing.word} lang={project?.src_lang} P={P}/>
          </Modal>
        )}

        {/* Закреплённые карточки */}
        {/* Открепить все — карточки остаются в проекте, убираются только с экрана */}
        {pinnedCards.length>0&&(
          <button onClick={()=>setPinnedCards([])} style={{position:"fixed",bottom:"calc(16px + env(safe-area-inset-bottom,0px))",right:"calc(16px + env(safe-area-inset-right,0px))",zIndex:210,display:"inline-flex",alignItems:"center",gap:6,minHeight:32,background:P.surface,border:`1px solid ${P.border}`,color:P.mid,borderRadius:16,padding:"0 12px",fontSize:12.5,fontWeight:500,cursor:"pointer",fontFamily:SANS}}><Icon n="close" s={14}/>{t("Открепить все")}</button>
        )}
        {pinnedCards.map(pin=>{
          const maxW=Math.min(window.innerWidth-20,Math.max(120,pin.w));
          const fs1=Math.max(13,Math.min(24,maxW/12+(pin.h?pin.h/28:0)));
          const fs2=Math.max(11,Math.min(19,maxW/15+(pin.h?pin.h/36:0)));
          return(
          <div key={pin.uid} style={{position:"fixed",
            left:Math.max(0,Math.min(window.innerWidth-maxW-4,pin.x)),
            top:Math.max(0,Math.min(window.innerHeight-80,pin.y)),
            width:maxW,
            background:P.glassCard,backdropFilter:"blur(14px)",
            border:`1px solid ${P.border}`,borderTop:`3px solid ${cardColorIn(P,pin.card.color)}`,
            borderRadius:9,boxShadow:`0 4px 22px ${P.shadowMd}`,zIndex:190,userSelect:"none",minWidth:120,
            height:pin.h?pin.h:undefined,overflow:"hidden",display:"flex",flexDirection:"column",
            willChange:"transform",touchAction:"none"}}>
            <div
              // Тянем от того места, где карточку видно (оно могло быть прижато к краю окна).
              onMouseDown={e=>{e.preventDefault();const r=e.currentTarget.parentElement.getBoundingClientRect();pinDragRef.current={uid:pin.uid,smx:e.clientX,smy:e.clientY,sx:r.left,sy:r.top};}}
              onTouchStart={e=>{const t=e.touches[0];const r=e.currentTarget.parentElement.getBoundingClientRect();pinDragRef.current={uid:pin.uid,smx:t.clientX,smy:t.clientY,sx:r.left,sy:r.top};}}
              style={{padding:"5px 8px",display:"flex",justifyContent:"space-between",alignItems:"center",cursor:"grab",borderBottom:`1px solid ${P.borderSoft}`,flexShrink:0}}>
              <span style={{fontSize:9,color:P.faint,textTransform:"uppercase",letterSpacing:".1em"}}><Icon n="pin" style={{marginRight:6}}/>{fmt(pin.card.time_sec)}</span>
              <button onClick={()=>unpinCard(pin.uid)} aria-label={t("Открепить")} title={t("Открепить")} className="ac-ibtn" style={{width:32,height:32,margin:"-2px -4px",flexShrink:0,display:"inline-flex",alignItems:"center",justifyContent:"center",background:"none",border:"none",borderRadius:6,color:P.mid,cursor:"pointer",padding:0}}><Icon n="close" s={13}/></button>
            </div>
            <div onClick={()=>togglePinFlip(pin.uid)} style={{padding:"8px 10px 12px",cursor:"pointer",flex:1,overflow:"hidden"}}>
              {!pin.flipped?(
                <>
                  <div style={{fontFamily:FONT,fontSize:fs1,color:legibleOn(cardColorIn(P,pin.card.color)||P.text,P.card),lineHeight:1.25,wordBreak:"break-word"}}>{pin.card.word}</div>
                  <div style={{fontSize:9,color:P.faint,marginTop:3}}>{t("нажмите для перевода")}</div>
                </>
              ):(
                <>
                  <div style={{fontSize:Math.max(9,fs2-3),color:P.mid,marginBottom:3,wordBreak:"break-word",lineHeight:1.3}}>{pin.card.word}</div>
                  <div style={{fontFamily:FONT,fontSize:fs2,color:P.text,fontWeight:700,lineHeight:1.3,wordBreak:"break-word"}}>{pin.card.translation||"—"}</div>
                </>
              )}
            </div>
            <div
              onMouseDown={e=>{e.preventDefault();pinResizeRef.current={uid:pin.uid,smx:e.clientX,smy:e.clientY,sw:pin.w,sh:pin.h||120};}}
              onTouchStart={e=>{const t=e.touches[0];pinResizeRef.current={uid:pin.uid,smx:t.clientX,smy:t.clientY,sw:pin.w,sh:pin.h||120};}}
              style={{position:"absolute",bottom:3,right:3,width:12,height:12,cursor:"se-resize",
                borderRight:`2px solid ${P.border}`,borderBottom:`2px solid ${P.border}`,borderRadius:"0 0 3px 0",opacity:0.5}}/>
          </div>
          );
        })}

        {showTextImport&&<TextImport onImport={handleTextImport} onClose={()=>setShowTextImport(false)}/>}

        <style key={theme}>{`
          *{box-sizing:border-box;margin:0;padding:0;-webkit-tap-highlight-color:transparent;}
          html,body{background:${P.pageBg};overscroll-behavior:none;}
          body{padding-bottom:env(safe-area-inset-bottom,0px);}
          input,textarea{font-family:${SANS};color:${P.text};}
          input::placeholder,textarea::placeholder{color:${P.faint};}
          input:focus,textarea:focus{outline:2px solid ${P.blueMid};outline-offset:1px;}
          button{font-family:${SANS};transition:opacity .12s,background .12s,border-color .12s,color .12s;}
          button:active{opacity:.7;}

          /* ── Safe Scroll Inset ───────────────────────────────────────────────
             --safe-scroll-inset: 5-8% «мёртвой зоны» с каждого бокового края.
             На мобиле пользователь держит телефон одной рукой — большой палец
             перекрывает ~7% экрана; эта зона защищает от случайных нажатий
             при скролле. На планшете отступ меньше; на десктопе = 0.
             --ssi — псевдоним для обратной совместимости с inline-стилями. */
          /* Отступы по краям одни на весь экран (16 px у страницы) — раньше часть блоков
             плеера сужалась на 7,5% и не стыковалась со списком карточек. */
          :root{--safe-scroll-inset:0px;--ssi:0px;}

          /* Транспортные кнопки и слайдеры: отступ от краёв */
          .safe-zone-h{padding-left:var(--safe-scroll-inset);padding-right:var(--safe-scroll-inset);}

          /* Список карточек: контейнер сужен, кнопки не у самого края */
          .cards-safe{padding-left:var(--safe-scroll-inset);padding-right:var(--safe-scroll-inset);}
          /* Внутри списка: рамка карточек тянется до края родителя (полноширинный дивайдер) */
          .cards-safe .cards-list{margin-left:calc(-1 * var(--safe-scroll-inset));margin-right:calc(-1 * var(--safe-scroll-inset));}
          /* Тулбар над списком — дополнительный отступ справа чтобы кнопки не прижимались к краю */
          .cards-safe-toolbar{padding-right:var(--safe-scroll-inset);}

          @media(max-width:480px){input,textarea{font-size:16px!important;}}
          .vol-slider,.spd-slider{-webkit-appearance:none;appearance:none;height:4px;background:${P.border};border-radius:4px;outline:none!important;border:none;margin:0;vertical-align:middle;}
          .vol-slider::-webkit-slider-thumb,.spd-slider::-webkit-slider-thumb{-webkit-appearance:none;appearance:none;width:14px;height:14px;border-radius:50%;background:${P.blue};cursor:pointer;border:none;}
          .vol-slider::-moz-range-thumb,.spd-slider::-moz-range-thumb{width:14px;height:14px;border-radius:50%;background:${P.blue};cursor:pointer;border:none;}
          .vol-slider:focus,.vol-slider:focus-visible,.spd-slider:focus,.spd-slider:focus-visible{outline:none!important;box-shadow:none!important;}
          .no-scrollbar::-webkit-scrollbar{display:none;}
          .no-scrollbar{scrollbar-width:none;}
          .cards-list::-webkit-scrollbar{width:4px;}
          .cards-list::-webkit-scrollbar-track{background:transparent;}
          .cards-list::-webkit-scrollbar-thumb{background:${P.border};border-radius:4px;}
          .cards-list::-webkit-scrollbar-thumb:hover{background:${P.faint};}
          .sidebar-scroll::-webkit-scrollbar{display:none;}
          .sidebar-scroll{scrollbar-width:none;-ms-overflow-style:none;}
          @keyframes ac-fade-in{from{opacity:0;transform:scale(0.97)}to{opacity:1;transform:scale(1)}}
        `}</style>
      </div>
    </div>
  );
}
